import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    Calendar, ChevronLeft, ChevronRight, Clock, MapPin, Edit2, Trash2,
    Check, X, Wallet, FileText, FilePlus2, FileEdit, Download, Pencil,
    Eye, ChevronRight as ChevronRightIcon, Plus, UserPlus, Send, Search,
    Mail, ChevronDown,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import PanelShell from './PanelShell';
import DocumentPreviewModal from '../DocumentPreviewModal';
import { debounce } from '../../utils/debounce';
import { calendarService } from '../../services/calendarService';
import contactService from '../../services/contactService';
import { documentService } from '../../services/documentService';
import useCurrencyPreference from '../../hooks/useCurrencyPreference';
import { getActiveTimezone } from '../../utils/timezone';
import PeoplePicker, { resolveInviteeUserIds, inviteUsersToEvent } from '../calendar/PeoplePicker';
import {
    AM, PM, parseTimeInput, normalizeTimeText, formatClock12, meridiemOf,
} from '../../utils/timeInput';
import { useLanguage } from '../../i18n';
import { seriesSummary } from '../../utils/calendarSeries';
import { PackageInvitationLines } from '../GuestPackageInvitationCard';
import Modal from '../ui/Modal';
import { userErrorMessage } from '../../utils/userError';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatDateRange = (start, end) => {
    if (!start) return '';
    const s = formatTime(start);
    if (!end) return s;
    return `${s} - ${formatTime(end)}`;
};

// Full calendar date as DD/MM/YYYY in the viewer's local timezone (Upcoming
// Events cards must always show the explicit date alongside the time — #235).
const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const formatAmount = (amount, currency = 'USD') => {
    if (amount == null) return '—';
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency || 'USD',
            maximumFractionDigits: 2,
        }).format(amount);
    } catch (_) {
        return `${currency || 'USD'} ${Number(amount).toFixed(2)}`;
    }
};

const dateKey = (year, month, day) => (
    `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
);

// "Time to be confirmed" rule: an event whose stored start resolves to local
// 00:00 is the sentinel for "user didn't specify a time" (the DB requires a
// start_time, so untimed events are pinned to local midnight of their day).
// Such events are shown as "Time to be confirmed" and sorted to the top of the
// day rather than at the 12:00 AM slot.
// "Time to be Confirmed" — the canonical two-part rule (#269), mirroring the BE's
// calendar_sharing.is_time_to_be_confirmed: a date-only event has NO end_time AND
// sits at local midnight. The end_time half is load-bearing — testing midnight
// alone put genuinely TIMED events (a real 00:00 booking, or one that renders at
// midnight because the browser tz differs from the tz it was created in) into the
// amber "Time to be confirmed" strip across every calendar surface.
const isTimePending = (event) => {
    if (!event?.start_time) return true;
    if (event.end_time) return false;
    try {
        const d = new Date(event.start_time);
        return d.getHours() === 0 && d.getMinutes() === 0;
    } catch (_) { return false; }
};

const TIME_TBC_LABEL = 'Time to be confirmed';

// ── Double-booking warning (#268) ───────────────────────────────────────────
// The BE rejects a manual create/reschedule that hard-overlaps an already
// CONFIRMED event with 409 {conflict:true, conflicts:[...]} — the same engine
// the Telegram bridges use. The Web UI used to book straight over the
// clash in silence; now we surface the conflict and let the user decide, and
// "Book anyway" re-sends the identical request with force:true.
//
// PORTALED to <body> (by <Modal>) deliberately: the Calendar lives inside a 3D flip card
// (perspective / preserve-3d / rotateY), and a transformed ancestor becomes the
// containing block for position:fixed — an in-tree dialog would be trapped and
// clipped inside the panel (the #225 pattern).
const ConflictDialog = ({ prompt, onCancel, onConfirm }) => {
    const { t } = useLanguage();
    if (!prompt) return null;
    const rows = Array.isArray(prompt.conflicts) ? prompt.conflicts : [];
    return (
        <Modal onClose={onCancel} zIndex="z-[70]" closeOnBackdrop={false} scrimTestId="event-conflict-dialog" cardClassName="p-5 space-y-3">
                <div className="flex items-start gap-2.5">
                    <Clock size={18} className="text-pai-amber mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                        <h3 className="text-[15px] font-bold text-white">
                            {t('caleSchedulingConflict', 'Scheduling conflict')}
                        </h3>
                        <p className="mt-1 text-[13px] text-pai-meta">
                            {t('caleConflictOverlapPeriod', 'This overlaps an event you have already confirmed.')}
                        </p>
                    </div>
                </div>
                <ul className="space-y-1.5" data-testid="event-conflict-list">
                    {rows.map((c) => (
                        <li
                            key={c.id}
                            data-testid="event-conflict-item"
                            className="rounded-row bg-pai-amber/[0.05] border border-pai-amber/30 px-3 py-2 text-[13px] text-pai-body break-words"
                        >
                            <span className="font-semibold text-white">{c.title || t('caleUntitledEvent', 'Untitled event')}</span>
                            {(c.start_local || c.end_local) && (
                                <span className="ml-1 text-pai-meta tnum">
                                    ({c.start_local}{c.end_local ? ` – ${c.end_local}` : ''})
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
                <div className="flex justify-end gap-2 pt-1">
                    <button
                        type="button"
                        data-testid="event-conflict-cancel"
                        onClick={onCancel}
                        className="px-3.5 py-2 text-sm font-semibold text-pai-secondary hover:text-white hover:bg-white/[0.07] rounded-ctl transition-colors"
                    >
                        {t('commonCancel', 'Cancel')}
                    </button>
                    <button
                        type="button"
                        data-testid="event-conflict-confirm"
                        onClick={onConfirm}
                        className="px-3.5 py-2 text-sm font-semibold text-pai-amber border border-pai-amber/50 bg-pai-amber/[0.1] hover:bg-pai-amber/[0.18] rounded-ctl transition-colors"
                    >
                        {t('caleBookAnyway', 'Book anyway')}
                    </button>
                </div>
            </Modal>
    );
};

// True when a rejected save came back as the BE's double-booking 409 (#268).
export const isConflictError = (err) => err?.status === 409 && !!err?.body?.conflict;

// While the Calendar panel is OPEN and the tab is visible, poll its data sources
// on this interval so a state change made through an external channel (e.g. a
// Telegram-created/updated/deleted event) — which fires NO browser window event
// — surfaces without a manual close/re-open (Ui calendar Update #245). The
// getActivity query is a small grouped read, so 15s polling while open is cheap.
const CALENDAR_POLL_MS = 15000;

// Structural equality via JSON so a poll that returns unchanged data is a true
// no-op setState (React skips the update → zero re-render, no flicker/jitter).
const sameJson = (a, b) => {
    try { return JSON.stringify(a) === JSON.stringify(b); } catch (_) { return false; }
};

// Local calendar-day key (YYYY-MM-DD) for a Date in the given IANA timezone.
// `en-CA` formats as YYYY-MM-DD and the `timeZone` option resolves the wall-clock
// day in the user's reference tz — so a TBC event pinned to local midnight is
// scoped to the day the user actually sees (Upcoming events Calendar #174).
const localDayKey = (date, tz) => {
    try {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
        }).format(date);
    } catch (_) {
        const p = (n) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
    }
};

// Pure calendar arithmetic on a YYYY-MM-DD string (tz/DST-free): add n days.
const addDaysToKey = (iso, n) => {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
    dt.setUTCDate(dt.getUTCDate() + n);
    const p = (x) => String(x).padStart(2, '0');
    return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
};

// Whether a TIMED event has already finished as of `nowMs` (Upcoming Events
// "Now & Next" real-time cleanup — #174). End is the event's end_time, or
// start + 1h when it has none (mirrors the BE calendar_sharing.find_conflicts
// convention). Date-only "Time to be confirmed" events have no real end and are
// handled by the caller — they must NOT be treated as finished.
const timedEventHasFinished = (event, nowMs) => {
    if (!event?.start_time) return false;
    const start = new Date(event.start_time);
    if (Number.isNaN(start.getTime())) return false;
    const end = event.end_time ? new Date(event.end_time) : new Date(start.getTime() + 60 * 60 * 1000);
    if (Number.isNaN(end.getTime())) return false;
    return nowMs > end.getTime();
};

// Prefer the server-derived `display_status` (guest acceptance drives the
// headline once anyone is invited), falling back to the event's own status.
const eventDisplayStatus = (event) => event?.display_status || event?.status || 'pending';

const StatusBadge = ({ status }) => {
    const { t } = useLanguage();
    // Solo events (no guests) carry display_status 'none' — no Pending/Confirmed
    // label; they're simply scheduled (Calendar "Pending" Label Logic bug).
    if (!status || status === 'none') return null;
    // Accent rule: a semantic colour may tint text, a 1px border or a background
    // wash at <=12% alpha — never fill a chip.
    const styles = {
        pending: 'bg-pai-amber/[0.1] border border-pai-amber/30 text-pai-amber',
        confirmed: 'bg-pai-green/[0.1] border border-pai-green/30 text-pai-green',
        declined: 'bg-white/[0.05] border border-pai-subtle text-pai-meta',
        cancelled: 'bg-pai-red/[0.1] border border-pai-red/30 text-pai-red',
    };
    const knownLabels = {
        pending: t('contactEventStatus_pending', 'Pending'),
        confirmed: t('contactEventStatus_confirmed', 'Confirmed'),
        declined: t('contactEventStatus_declined', 'Declined'),
        cancelled: t('caleStatusCancelled', 'Cancelled'),
    };
    const label = knownLabels[status] || (status.charAt(0).toUpperCase() + status.slice(1));
    return (
        <span data-testid="event-status-badge" className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[status] || styles.pending}`}>
            {label}
        </span>
    );
};

// #532: an occurrence of a repeating event (rows of one series share series_id).
const SeriesBadge = ({ event }) => {
    const { t } = useLanguage();
    if (!event?.series_id) return null;
    return (
        <span data-testid="event-series-badge" className="text-xs font-medium px-2 py-0.5 rounded-full bg-white/[0.05] border border-pai-subtle text-pai-meta">
            {t('caleSeriesBadge')}
        </span>
    );
};

const ATTENDANCE_STATUS_STYLES = {
    confirmed: 'text-pai-green',
    pending: 'text-pai-amber',
    declined: 'text-pai-meta',
};
const ATTENDANCE_STATUS_LABELS = { confirmed: 'Confirmed', pending: 'Pending', declined: 'Declined' };
// t() key for each attendance status, mirroring ATTENDANCE_STATUS_LABELS'
// English fallback exactly (reused from the Contacts Hub vocabulary).
const attendanceStatusLabel = (status, t) => {
    const key = `contactEventStatus_${status}`;
    return t(key, ATTENDANCE_STATUS_LABELS[status] || ATTENDANCE_STATUS_LABELS.pending);
};

// Build the per-guest rows the dropdown lists. `invitations` is authoritative
// (it carries each guest's id + status + team provenance); the flat
// `attendance` name arrays are a resilience fallback for any payload that
// predates / omits it, so the dropdown can never regress to a nameless tally.
const attendanceRows = (attendance, invitations) => {
    const list = invitations || [];
    if (list.length) {
        return list.map((i) => ({
            key: i.id,
            name: i.invitee_name,
            status: (i.status || 'pending'),
            team: i.team_name || null,
        }));
    }
    const { confirmed = [], pending = [], declined = [] } = attendance || {};
    return [
        ...confirmed.map((n, idx) => ({ key: `c${idx}`, name: n, status: 'confirmed', team: null })),
        ...pending.map((n, idx) => ({ key: `p${idx}`, name: n, status: 'pending', team: null })),
        ...declined.map((n, idx) => ({ key: `d${idx}`, name: n, status: 'declined', team: null })),
    ];
};

// Guest attendance at a glance (#265 Identity Attribution / #267 Granular
// Multi-Invite). Two shapes, both name-first — the old cryptic "✓2 · ⧗1" tally
// is gone, because a count tells an organizer nothing about WHO still owes them
// an answer:
//   • ONE guest  → the named state, e.g. "Pending confirmation from Jorge
//     Cirera" / "Jorge Cirera confirmed". A dropdown over a single row would be
//     pure friction, so the name is shown outright.
//   • 2+ guests (incl. whole-team events, whose fan-out creates one real
//     invitation per member) → a "Multi-Invite Management" toggle whose summary
//     reads in words ("1 of 3 confirmed") and which expands to every invited
//     individual by name with their own status.
const AttendanceBadge = ({ attendance, invitations }) => {
    const { t } = useLanguage();
    const [open, setOpen] = useState(false);
    if (!attendance || !attendance.total_invited) return null;
    const { confirmed = [], pending = [], declined = [], total_invited } = attendance;

    // 1-on-1: surface the single guest's state with their name.
    if (total_invited === 1) {
        const single = (
            pending.length === 1
                ? { text: t('caleAttendancePendingFrom', 'Pending confirmation from {name}', { name: pending[0] }), cls: 'bg-pai-amber/[0.1] border border-pai-amber/30 text-pai-amber' }
                : confirmed.length === 1
                    ? { text: t('caleAttendanceConfirmedName', '{name} confirmed', { name: confirmed[0] }), cls: 'bg-pai-green/[0.1] border border-pai-green/30 text-pai-green' }
                    : { text: t('caleAttendanceDeclinedName', '{name} declined', { name: declined[0] }), cls: 'bg-white/[0.05] border border-pai-subtle text-pai-meta' }
        );
        return (
            <span
                data-testid="event-attendance-label"
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${single.cls}`}
            >
                {single.text}
            </span>
        );
    }

    const rows = attendanceRows(attendance, invitations);
    return (
        <span data-testid="event-attendance" className="inline-flex flex-col items-start min-w-0 max-w-full">
            <button
                type="button"
                data-testid="event-attendance-toggle"
                aria-expanded={open}
                aria-label={t('caleGuestListAria', 'Guest list — {confirmed} of {total} confirmed', { confirmed: confirmed.length, total: total_invited })}
                title={t('caleShowGuestList', 'Show every guest and their status')}
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-white/[0.06] border border-pai-subtle text-pai-secondary hover:bg-white/[0.1] hover:text-white transition-colors"
            >
                <span className="truncate tnum">{t('caleConfirmedOfTotal', '{confirmed} of {total} confirmed', { confirmed: confirmed.length, total: total_invited })}</span>
                <ChevronDown size={12} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <ul
                    data-testid="event-attendance-list"
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1 w-full min-w-0 max-h-40 overflow-y-auto rounded-row border border-pai bg-pai-modal p-1.5 space-y-0.5"
                >
                    {rows.map((r) => (
                        <li
                            key={r.key}
                            data-testid="event-attendance-row"
                            data-status={r.status}
                            className="flex items-center justify-between gap-2 px-1 min-w-0"
                        >
                            <span className="text-xs text-pai-secondary truncate">
                                {r.name}
                                {r.team && (
                                    <span className="ml-1 text-[10px] text-pai-faint">{t('caleViaTeam', 'via {team}', { team: r.team })}</span>
                                )}
                            </span>
                            <span className={`flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide ${ATTENDANCE_STATUS_STYLES[r.status] || ATTENDANCE_STATUS_STYLES.pending}`}>
                                {attendanceStatusLabel(r.status, t)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </span>
    );
};

// Guests section — Confirmed / Pending / Declined lists, each guest with a
// Revoke (×) button so the organizer can un-invite them (Remove Event
// Invitation card). Renders from the event's `invitations` (which carry the
// invitation id + status) so revoke can target the exact row.
const AttendanceBreakdown = ({ invitations, onRevoke, revokingId }) => {
    const { t } = useLanguage();
    const list = invitations || [];
    if (!list.length) return null;
    const groups = [
        { key: 'confirmed', label: t('contactEventStatus_confirmed', 'Confirmed'), color: 'text-pai-green' },
        { key: 'pending', label: t('caleGuestsPendingConfirmation', 'Pending confirmation'), color: 'text-pai-amber' },
        { key: 'declined', label: t('contactEventStatus_declined', 'Declined'), color: 'text-pai-meta' },
    ];
    return (
        <div className="border-t border-pai-hairline pt-2 space-y-2">
            <div className="pai-eyebrow text-[10px]">{t('caleGuestsHeading', 'Guests')}</div>
            {groups.map(({ key, label, color }) => {
                const rows = list.filter((i) => (i.status || 'pending') === key);
                if (!rows.length) return null;
                return (
                    <div key={key}>
                        <div className={`text-[10px] font-semibold uppercase tracking-wide ${color}`}>
                            {label} ({rows.length})
                        </div>
                        <ul className="mt-0.5 space-y-0.5">
                            {rows.map((inv) => (
                                <li key={inv.id} className="flex items-center justify-between gap-2 group">
                                    <span className="text-xs text-pai-secondary truncate">{inv.invitee_name}</span>
                                    {onRevoke && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onRevoke(inv); }}
                                            disabled={revokingId === inv.id}
                                            title={t('caleRemoveGuest', 'Remove {name}', { name: inv.invitee_name })}
                                            aria-label={t('caleRemoveGuest', 'Remove {name}', { name: inv.invitee_name })}
                                            className="flex-shrink-0 text-pai-meta hover:text-pai-red disabled:opacity-40 transition-colors"
                                        >
                                            <X size={13} />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>
    );
};

const EventCard = ({ event, onUpdate, onDelete, onOpen = null, expandable = false, focused = false }) => {
    const { t } = useLanguage();
    const [editing, setEditing] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [editTitle, setEditTitle] = useState(event.title);
    const [editLocation, setEditLocation] = useState(event.location || '');
    const [editStartTime, setEditStartTime] = useState(event.start_time?.slice(0, 16) || '');
    const [editEndTime, setEditEndTime] = useState(event.end_time?.slice(0, 16) || '');
    const cardRef = useRef(null);

    useEffect(() => {
        if (focused && cardRef.current) {
            const t = setTimeout(() => {
                cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 80);
            return () => clearTimeout(t);
        }
    }, [focused]);

    const handleSave = async () => {
        await onUpdate(event.id, {
            title: editTitle,
            location: editLocation || null,
            start_time: editStartTime || null,
            end_time: editEndTime || null,
        });
        setEditing(false);
    };

    const handleCancel = () => {
        setEditTitle(event.title);
        setEditLocation(event.location || '');
        setEditStartTime(event.start_time?.slice(0, 16) || '');
        setEditEndTime(event.end_time?.slice(0, 16) || '');
        setEditing(false);
    };

    const isDeclined = ['declined', 'cancelled'].includes(eventDisplayStatus(event));

    if (editing) {
        return (
            <div className="rounded-row border border-pai bg-white/[0.05] p-3 space-y-2">
                <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="pai-input w-full text-sm px-2.5 py-1.5"
                    placeholder={t('caleEventTitlePlaceholder', 'Event title')}
                />
                <div className="grid grid-cols-2 gap-2">
                    <input
                        type="datetime-local"
                        value={editStartTime}
                        onChange={(e) => setEditStartTime(e.target.value)}
                        className="pai-input text-xs px-2.5 py-1.5 tnum [color-scheme:dark]"
                    />
                    <input
                        type="datetime-local"
                        value={editEndTime}
                        onChange={(e) => setEditEndTime(e.target.value)}
                        className="pai-input text-xs px-2.5 py-1.5 tnum [color-scheme:dark]"
                    />
                </div>
                <input
                    type="text"
                    value={editLocation}
                    onChange={(e) => setEditLocation(e.target.value)}
                    className="pai-input w-full text-xs px-2.5 py-1.5"
                    placeholder={t('caleLocationOptionalPlaceholder', 'Location (optional)')}
                />
                <div className="flex items-center gap-1 justify-end">
                    <button onClick={handleCancel} className="text-pai-meta hover:text-white p-1 rounded transition-colors">
                        <X size={14} />
                    </button>
                    <button onClick={handleSave} className="text-pai-green hover:text-white p-1 rounded transition-colors">
                        <Check size={14} />
                    </button>
                </div>
            </div>
        );
    }

    // Clicking the card opens its detail pop-up (#272 Upcoming Events Box) — the
    // list used to be entirely static. `expandable` (the older inline
    // description toggle) is kept for any caller that doesn't pass onOpen.
    const handleCardClick = () => {
        if (onOpen) { onOpen(event); return; }
        if (expandable) setExpanded((v) => !v);
    };
    const clickable = Boolean(onOpen) || expandable;

    return (
        <div
            ref={cardRef}
            data-testid="calendar-event-card"
            onClick={handleCardClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); }
            } : undefined}
            className={`group rounded-row border bg-white/[0.03] p-3 flex items-start justify-between gap-2 transition-colors ${
                focused
                    ? 'border-white/80 bg-white/[0.055] shadow-glow animate-pulse-once'
                    : 'border-pai'
            } ${clickable ? 'cursor-pointer hover:bg-white/[0.07]' : ''} ${expanded ? 'bg-white/[0.06]' : ''}`}
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[15px] font-semibold ${isDeclined ? 'line-through text-pai-faint' : 'text-white'}`}>
                        {event.title}
                    </span>
                    <StatusBadge status={eventDisplayStatus(event)} />
                    <SeriesBadge event={event} />
                    <AttendanceBadge attendance={event.attendance} invitations={event.invitations} />
                </div>
                <div className="flex items-center gap-1.5 text-[13px] mt-1 flex-wrap">
                    <Clock size={12} className={isTimePending(event) ? 'text-pai-amber' : 'text-pai-meta'} />
                    {isTimePending(event) ? (
                        <span className="text-pai-amber font-medium">{t('caleTimeTbc', TIME_TBC_LABEL)}</span>
                    ) : (
                        <span className="text-pai-meta tnum">{formatDateRange(event.start_time, event.end_time)}</span>
                    )}
                    {/* Full date, always visible on every card (#235). */}
                    <span className="text-pai-faint">·</span>
                    <span
                        data-testid="upcoming-event-date"
                        className="text-pai-meta tabular-nums"
                    >
                        {formatFullDate(event.start_time)}
                    </span>
                </div>
                {event.location && (
                    <div className="flex items-center gap-1.5 text-[13px] text-pai-meta mt-1">
                        <MapPin size={12} />
                        <span className="truncate">{event.location}</span>
                    </div>
                )}
                {expanded && event.description && (
                    <p className="mt-2 text-[13px] text-pai-secondary whitespace-pre-wrap">
                        {event.description}
                    </p>
                )}
            </div>
            {/* Edit (pencil) + Delete (bin) — HIDDEN until the card is hovered,
                then WHITE; each turns to its accent colour (blue / red) only when
                the pointer is directly over it (#235). stopPropagation keeps an
                icon click from triggering the card's own click. */}
            <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <button
                    data-testid="upcoming-event-edit"
                    onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                    // NOTE: `dark:text-white` + `hover:text-blue-500` are asserted as
                    // LITERAL class strings by upcoming-events-pencil-bin.spec.js, so
                    // they survive the dark-only cleanup. In dark mode the computed
                    // colour is already the design's white resting state.
                    className="opacity-0 group-hover:opacity-100 pai-touch-reveal transition-opacity text-gray-500 dark:text-white hover:text-blue-500 dark:hover:text-blue-400 p-1 rounded"
                    title={t('commonEdit', 'Edit')}
                >
                    <Edit2 size={12} />
                </button>
                <button
                    data-testid="upcoming-event-delete"
                    onClick={(e) => { e.stopPropagation(); onDelete(event.id); }}
                    // See the pencil above: these literal classes are a spec contract.
                    className="opacity-0 group-hover:opacity-100 pai-touch-reveal transition-opacity text-gray-500 dark:text-white hover:text-red-500 dark:hover:text-red-400 p-1 rounded"
                    title={t('commonDelete', 'Delete')}
                >
                    <Trash2 size={12} />
                </button>
            </div>
        </div>
    );
};

// ── Upcoming-event detail pop-up (#272 Upcoming Events Box) ─────────────────
// The "Upcoming Events" list was static: a card showed a title, a time and two
// hover icons, and everything else about the event — its notes, who is coming,
// who still owes an answer — required navigating into the month grid, opening
// the day, then the event. This pop-up puts all of that (plus a self-contained
// Invite search) one click away from the dashboard.
//
// PORTALED to <body> for the same reason as ConflictDialog: the Calendar lives
// inside a 3D flip card (perspective / preserve-3d / rotateY), and a transformed
// ancestor becomes the containing block for position:fixed, so an in-tree modal
// would be trapped and clipped inside the panel (the #225 pattern).
const UpcomingEventDetail = ({ event, onClose, onInvite }) => {
    const { t } = useLanguage();
    const [showInvite, setShowInvite] = useState(false);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [revokingId, setRevokingId] = useState(null);
    const [revokedIds, setRevokedIds] = useState([]);

    // Escape dismisses the TOP layer only: <Modal> handles it in the CAPTURE
    // phase and stops it, so PanelShell's bubble-phase listener never tears the
    // Calendar panel down behind the pop-up.

    if (!event) return null;
    const pending = isTimePending(event);
    const guests = (event.invitations || []).filter((i) => !revokedIds.includes(i.id));

    // A pick invites IMMEDIATELY — the card's "clicking a suggested contact or
    // team should immediately add them to the invitation list for that event".
    const handlePick = async (entry) => {
        if (busy) return;
        setBusy(true);
        setStatus(null);
        try {
            const res = await onInvite(event.id, [entry]);
            if (res?.failed?.length) setStatus(t('caleCouldntInviteName', "Couldn't invite {name}.", { name: entry.name }));
            else if (res?.invited) setStatus(t('caleInvitedName', 'Invited {name}.', { name: entry.name }));
            else setStatus(t('caleAlreadyOnGuestList', '{name} is already on the guest list.', { name: entry.name }));
        } catch (_) {
            setStatus(t('caleCouldntInviteName', "Couldn't invite {name}.", { name: entry.name }));
        } finally {
            setBusy(false);
        }
    };

    const handleRevoke = async (inv) => {
        if (!inv?.id || revokingId) return;
        setRevokingId(inv.id);
        try {
            await calendarService.revokeInvitation(inv.id);
            setRevokedIds((prev) => [...prev, inv.id]);
            setStatus(t('caleRemovedFromGuestList', 'Removed {name} from the guest list.', { name: inv.invitee_name }));
            window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated'));
        } catch (_) {
            setStatus(t('caleCouldntRemoveGuest', "Couldn't remove {name}.", { name: inv.invitee_name }));
        } finally {
            setRevokingId(null);
        }
    };

    return (
        <Modal onClose={onClose} zIndex="z-[80]" layout="column" cardClassName="max-h-[85dvh]" cardTestId="upcoming-event-detail">
                <div className="flex items-start justify-between gap-2 p-5 border-b border-pai-hairline">
                    <div className="min-w-0">
                        <h3
                            data-testid="upcoming-event-title"
                            className="text-[19px] font-bold tracking-[-0.015em] text-white break-words"
                        >
                            {event.title}
                        </h3>
                        <div className="mt-1.5 flex items-center gap-1.5 text-[13px] flex-wrap">
                            <Clock size={12} className={pending ? 'text-pai-amber' : 'text-pai-meta'} />
                            {pending ? (
                                <span className="text-pai-amber font-medium">{t('caleTimeTbc', TIME_TBC_LABEL)}</span>
                            ) : (
                                <span className="text-pai-meta tnum">{formatDateRange(event.start_time, event.end_time)}</span>
                            )}
                            <span className="text-pai-faint">·</span>
                            <span className="text-pai-meta tabular-nums">{formatFullDate(event.start_time)}</span>
                        </div>
                        {event.location && (
                            <div className="mt-1 flex items-center gap-1.5 text-[13px] text-pai-meta">
                                <MapPin size={12} className="flex-shrink-0" />
                                <span className="break-words">{event.location}</span>
                            </div>
                        )}
                    </div>
                    <button
                        data-testid="upcoming-event-close"
                        onClick={onClose}
                        aria-label={t('commonClose', 'Close')}
                        className="pai-icon-btn flex-shrink-0 w-[38px] h-[38px]"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-5 min-h-0">
                    {/* Notes */}
                    <section>
                        <div className="pai-eyebrow text-[10px] mb-1.5">
                            {t('caleNotesHeading', 'Notes')}
                        </div>
                        <p
                            data-testid="upcoming-event-notes"
                            className="text-[15px] leading-relaxed text-pai-body whitespace-pre-wrap break-words"
                        >
                            {event.description || t('caleNoNotesForEvent', 'No notes for this event.')}
                        </p>
                    </section>

                    {/* Attendees */}
                    <section>
                        <div className="pai-eyebrow text-[10px] mb-1.5">
                            {t('caleAttendeesHeading', 'Attendees')}
                        </div>
                        {guests.length === 0 ? (
                            <p data-testid="upcoming-event-attendees-empty" className="text-[15px] text-pai-meta">
                                {t('caleNoOneInvitedYet', 'No one invited yet.')}
                            </p>
                        ) : (
                            <ul data-testid="upcoming-event-attendees" className="space-y-1">
                                {guests.map((inv) => (
                                    <li
                                        key={inv.id}
                                        data-testid="upcoming-event-attendee"
                                        data-status={inv.status || 'pending'}
                                        className="flex items-center justify-between gap-2 min-w-0"
                                    >
                                        <span className="text-[15px] text-pai-secondary truncate">
                                            {inv.invitee_name}
                                            {inv.team_name && (
                                                <span className="ml-1 text-[10px] text-pai-faint">{t('caleViaTeam', 'via {team}', { team: inv.team_name })}</span>
                                            )}
                                        </span>
                                        <span className="flex items-center gap-1.5 flex-shrink-0">
                                            <span className={`text-[10px] font-semibold uppercase tracking-wide ${ATTENDANCE_STATUS_STYLES[inv.status] || ATTENDANCE_STATUS_STYLES.pending}`}>
                                                {attendanceStatusLabel(inv.status, t)}
                                            </span>
                                            <button
                                                data-testid="upcoming-attendee-remove"
                                                onClick={() => handleRevoke(inv)}
                                                disabled={revokingId === inv.id}
                                                aria-label={t('caleRemoveGuest', 'Remove {name}', { name: inv.invitee_name })}
                                                title={t('caleRemoveGuest', 'Remove {name}', { name: inv.invitee_name })}
                                                className="text-pai-meta hover:text-pai-red disabled:opacity-40 transition-colors"
                                            >
                                                <X size={12} />
                                            </button>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* Self-contained invite management */}
                    <section>
                        {!showInvite ? (
                            <button
                                data-testid="upcoming-event-invite-button"
                                onClick={() => { setShowInvite(true); setStatus(null); }}
                                className="pai-btn-secondary"
                            >
                                <UserPlus size={14} /> {t('caleInviteBtn', 'Invite')}
                            </button>
                        ) : (
                            <div>
                                <div className="pai-eyebrow text-[10px] mb-1.5">
                                    {t('caleInviteContactOrTeam', 'Invite a contact or team')}
                                </div>
                                <PeoplePicker
                                    testId="upcoming-invite"
                                    selected={[]}
                                    onSelect={handlePick}
                                    autoFocus
                                    busy={busy}
                                    placeholder={t('caleSearchNameEmailTeam', 'Search name, email or team…')}
                                />
                            </div>
                        )}
                        {status && (
                            <p data-testid="upcoming-invite-status" className="mt-2 text-[13px] text-pai-secondary">
                                {status}
                            </p>
                        )}
                    </section>
                </div>
            </Modal>
    );
};

// ── Doc row inside the day-detail card ───────────────────────────────────
const DocumentRow = ({ doc, onPreview, onDownload, onRename }) => {
    const { t } = useLanguage();
    const [renaming, setRenaming] = useState(false);
    const [draftName, setDraftName] = useState(doc.filename);
    const [saving, setSaving] = useState(false);

    const handleSaveRename = async (e) => {
        e?.stopPropagation();
        if (!draftName.trim() || draftName === doc.filename) {
            setRenaming(false);
            return;
        }
        setSaving(true);
        try {
            await onRename(doc, draftName.trim());
            setRenaming(false);
        } catch (_) {
            // keep open so the user can adjust
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            onClick={() => !renaming && onPreview(doc)}
            className="rounded-row border border-pai-blue/30 bg-pai-blue/[0.04] p-2.5 flex items-center gap-2 hover:bg-pai-blue/[0.09] cursor-pointer transition-colors"
        >
            <FileText size={14} className="text-pai-blue flex-shrink-0" />
            {renaming ? (
                <input
                    autoFocus
                    type="text"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveRename(e);
                        else if (e.key === 'Escape') {
                            e.stopPropagation();
                            setDraftName(doc.filename);
                            setRenaming(false);
                        }
                    }}
                    onBlur={handleSaveRename}
                    disabled={saving}
                    className="pai-input flex-1 min-w-0 text-xs rounded-chip px-2 py-1"
                />
            ) : (
                <span className="flex-1 min-w-0 truncate text-[13px] text-pai-body" title={doc.filename}>
                    {doc.filename}
                    {doc.version > 1 && (
                        <span className="ml-1 text-[10px] text-pai-faint tnum">v{doc.version}</span>
                    )}
                </span>
            )}
            <div
                className="flex items-center gap-0.5 flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={() => onPreview(doc)}
                    className="text-pai-meta hover:text-pai-blue p-1 rounded transition-colors"
                    title={t('caleFilePreview', 'Preview')}
                >
                    <Eye size={12} />
                </button>
                <button
                    onClick={() => onDownload(doc)}
                    className="text-pai-meta hover:text-pai-blue p-1 rounded transition-colors"
                    title={t('caleFileDownload', 'Download')}
                >
                    <Download size={12} />
                </button>
                <button
                    onClick={() => { setDraftName(doc.filename); setRenaming(true); }}
                    className="text-pai-meta hover:text-pai-blue p-1 rounded transition-colors"
                    title={t('caleFileRename', 'Rename')}
                >
                    <Pencil size={12} />
                </button>
            </div>
        </div>
    );
};

// ── Helpers for the multi-screen day-detail navigator ────────────────────

const formatUsageDuration = (ms) => {
    if (!ms || ms <= 0) return '0m';
    const totalMin = Math.round(ms / 60000);
    if (totalMin < 60) return `${totalMin}m`;
    const hours = Math.floor(totalMin / 60);
    const mins = totalMin % 60;
    // Always show minutes alongside hours so the "Blue Dot" reads e.g. "7h 00m".
    return `${hours}h ${String(mins).padStart(2, '0')}m`;
};

// Compact event row used inside EventsScreen.
const EventListRow = ({ event, onClick }) => {
    const { t } = useLanguage();
    const pending = isTimePending(event);
    const declined = ['declined', 'cancelled'].includes(eventDisplayStatus(event));
    return (
        <button
            onClick={onClick}
            data-testid="event-list-row"
            className={`w-full text-left rounded-row border border-pai bg-white/[0.03] p-2.5 flex items-center gap-2 hover:bg-white/[0.07] hover:border-pai-strong transition-colors ${declined ? 'opacity-60' : ''}`}
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[15px] font-semibold text-white truncate ${declined ? 'line-through' : ''}`}>
                        {event.title}
                    </span>
                    <StatusBadge status={eventDisplayStatus(event)} />
                    <SeriesBadge event={event} />
                    <AttendanceBadge attendance={event.attendance} invitations={event.invitations} />
                </div>
                <div className="flex items-center gap-2 mt-1 text-[12.5px] text-pai-meta flex-wrap">
                    {pending ? (
                        <span className="inline-flex items-center gap-1 text-pai-amber font-medium">
                            <Clock size={11} />
                            {t('caleTimeTbc', TIME_TBC_LABEL)}
                        </span>
                    ) : event.start_time && (
                        <span className="inline-flex items-center gap-1 tnum">
                            <Clock size={11} />
                            {formatDateRange(event.start_time, event.end_time)}
                        </span>
                    )}
                    {event.location && (
                        <span className="inline-flex items-center gap-1 truncate max-w-[140px]">
                            <MapPin size={11} />
                            {event.location}
                        </span>
                    )}
                </div>
            </div>
            <ChevronRightIcon size={14} className="text-white/50 flex-shrink-0" />
        </button>
    );
};

// Reusable header for every screen inside the day-detail navigator. When
// onPrevDay/onNextDay are supplied (the Daily View "menu" screen), the date is
// centered with Left/Right arrows flanking it for sequential day browsing
// (Date arrows ticket).
const ScreenHeader = ({ title, subtitle, onBack, onClose, right = null, onPrevDay = null, onNextDay = null }) => {
    const { t } = useLanguage();
    const dayNav = !!(onPrevDay && onNextDay);
    return (
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-pai-hairline">
            {/* Left slot (flex-1 to keep the centered nav truly centered). */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
                {onBack && (
                    <button
                        onClick={onBack}
                        className="p-1 text-pai-meta hover:text-white rounded hover:bg-white/[0.07] transition-colors"
                        title={t('commonBack', 'Back')}
                    >
                        <ChevronLeft size={16} />
                    </button>
                )}
                {!dayNav && (
                    <div className="min-w-0">
                        <div className="text-[15px] font-bold text-white truncate">{title}</div>
                        {subtitle && (
                            <div className="text-[12.5px] text-pai-meta truncate">{subtitle}</div>
                        )}
                    </div>
                )}
            </div>

            {/* Centered date + flanking day-navigation arrows (close to the text). */}
            {dayNav && (
                // #432: below 1280px it may shrink — its subtitle (the day's net,
                // which can be a long figure) wraps between "Net" and the amount
                // instead of pushing the close button out of the header on a phone.
                // At desktop widths it is exactly as before.
                <div className="flex items-center gap-1.5 flex-shrink-0 max-[1279px]:flex-shrink max-[1279px]:min-w-0">
                    <button
                        onClick={onPrevDay}
                        className="p-1 text-pai-meta hover:text-white rounded hover:bg-white/[0.07] transition-colors"
                        title={t('caleDayPrev', 'Previous day')}
                        aria-label={t('caleDayPrev', 'Previous day')}
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <div className="text-center min-w-0">
                        <div data-testid="day-detail-date" className="text-[17px] font-bold tracking-[-0.015em] text-white whitespace-nowrap tnum">{title}</div>
                        {subtitle && <div className="text-[12.5px] whitespace-nowrap max-[1279px]:whitespace-normal tnum">{subtitle}</div>}
                    </div>
                    <button
                        onClick={onNextDay}
                        className="p-1 text-pai-meta hover:text-white rounded hover:bg-white/[0.07] transition-colors"
                        title={t('caleDayNext', 'Next day')}
                        aria-label={t('caleDayNext', 'Next day')}
                    >
                        <ChevronRightIcon size={18} />
                    </button>
                </div>
            )}

            {/* Right slot (flex-1, right-aligned). */}
            <div className="flex items-center gap-1 flex-shrink-0 flex-1 justify-end">
                {right}
                {onClose && (
                    <button
                        data-testid="day-detail-close"
                        onClick={onClose}
                        className="p-1 text-pai-meta hover:text-white rounded hover:bg-white/[0.07] transition-colors"
                        title={t('caleCloseBackToMonth', 'Close (back to month)')}
                    >
                        <X size={16} />
                    </button>
                )}
            </div>
        </div>
    );
};

// ── Day Detail — Hourly Grid + secondary modules (Hourly grid #176) ──────
// The day-detail landing screen is a professional scrollable daily timeline
// (00:00–23:59, ~10 hours visible) dominating the view (70%), with compact
// Finance (10%) and PAI (10%) module boxes beneath — both preserving their
// click-through into the finance / PAI screens.
// Taller hour rows so short back-to-back events stay readable (Day fixes V2
// #233 — was 40). Combined with the day-detail now filling the whole panel
// height (see DayDetailCard), the visible span widens to ~11h+ while each hour
// block is roomier.
const HOUR_PX = 48;                       // pixel height of one hour row
const GRID_LABEL_GUTTER = 44;             // px reserved on the left for HH:00 labels
// A timed block shorter than this can't fit the title + time stacked on two
// lines, so the time range is appended to the title on ONE line instead (#233
// "Crunch" fix). ~2 text lines (title text-[11px] + time text-[9px]) + padding.
const EVENT_STACK_MIN_PX = 34;

const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;

// Compact 24h time (HH:MM) / range for the on-grid event blocks — matches the
// grid's 24h hour labels (Day fixes V2 #233).
const time24 = (dateStr) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getHours())}:${p(d.getMinutes())}`;
};
const formatRange24 = (start, end) => {
    if (!start) return '';
    const s = time24(start);
    return end ? `${s} - ${time24(end)}` : s;
};

// Minute-of-day span for a TIMED event (untimed/TBC events never reach here).
// A missing end defaults to +1h; an end that resolves before the start (event
// crosses midnight / malformed) is clamped to end-of-day so a block never
// renders with a negative height.
const eventMinutes = (event) => {
    const s = new Date(event.start_time);
    const startMin = s.getHours() * 60 + s.getMinutes();
    let endMin;
    if (event.end_time) {
        const e = new Date(event.end_time);
        endMin = e.getHours() * 60 + e.getMinutes();
        if (endMin <= startMin) endMin = 24 * 60;
    } else {
        endMin = startMin + 60;
    }
    return { startMin, endMin: Math.min(endMin, 24 * 60) };
};

// Greedy column packing so overlapping events sit side-by-side (each cluster of
// mutually-overlapping events splits the width) instead of stacking on top of
// each other. Returns Map(event.id → { col, cols }).
const layoutTimedEvents = (events) => {
    const items = events
        .map((ev) => ({ ev, ...eventMinutes(ev) }))
        .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
    const out = new Map();
    let cluster = [];
    let clusterEnd = -1;
    const flush = () => {
        const colEnds = [];
        for (const item of cluster) {
            let placed = false;
            for (let ci = 0; ci < colEnds.length; ci += 1) {
                if (colEnds[ci] <= item.startMin) { colEnds[ci] = item.endMin; item.col = ci; placed = true; break; }
            }
            if (!placed) { item.col = colEnds.length; colEnds.push(item.endMin); }
        }
        const cols = colEnds.length;
        cluster.forEach((item) => out.set(item.ev.id, { col: item.col, cols }));
        cluster = [];
        clusterEnd = -1;
    };
    for (const item of items) {
        if (cluster.length && item.startMin >= clusterEnd) flush();
        cluster.push(item);
        clusterEnd = Math.max(clusterEnd, item.endMin);
    }
    if (cluster.length) flush();
    return out;
};

// Compact secondary module box (Finance / PAI) — a STICKY, fixed-height footer
// row locked to the bottom of the day-detail stack (flex-shrink-0, no longer
// flex-1), each a full click-through to its screen. Being `w-full` it tracks the
// panel width automatically (Day fixes V2 #233).
const DayModuleBox = ({ Icon, label, sub, value, valueClass, accent, testId, onClick }) => (
    <button
        onClick={onClick}
        data-testid={testId}
        // ⚠️ GEOMETRY CONTRACT (day-fixes-v2 #233): `flex-shrink-0 w-full`, zero
        // gap between the two boxes and no horizontal margin — the spec asserts
        // the PAI box is exactly container-width, sits flush against the Finance
        // box, and that the grid above still clears 11 hours. Restyle the
        // surface, never the box model.
        className={`flex-shrink-0 w-full flex items-center gap-2.5 px-3 py-2 text-left border-t ${accent.border} ${accent.bg} hover:bg-white/[0.07] transition-colors`}
    >
        <div className={`w-8 h-8 rounded-ctl flex items-center justify-center flex-shrink-0 border ${accent.iconBg}`}>
            <Icon size={16} className={accent.icon} />
        </div>
        <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white">{label}</div>
            <div className="text-[11px] text-pai-meta truncate">{sub}</div>
        </div>
        <div className={`text-sm font-bold tnum ${valueClass}`}>{value}</div>
        <ChevronRightIcon size={14} className="text-white/50 flex-shrink-0" />
    </button>
);

const DayTimeline = ({ activity, dateIso, onOpenEvent, onPickFinance, onPickPai }) => {
    const { t } = useLanguage();
    const events = useMemo(() => activity?.events || [], [activity?.events]);
    const timed = useMemo(() => events.filter((e) => !isTimePending(e)), [events]);
    const tbc = useMemo(() => events.filter(isTimePending), [events]);
    const layout = useMemo(() => layoutTimedEvents(timed), [timed]);
    const scrollRef = useRef(null);

    // Frame the grid on the day's activity: scroll to an hour before the
    // earliest timed event (else 07:00, matching the design reference). Re-runs
    // when the day changes (day-navigation arrows) or events load.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        let hour = 7;
        if (timed.length) {
            const earliest = Math.min(...timed.map((e) => eventMinutes(e).startMin));
            hour = Math.max(0, Math.floor(earliest / 60) - 1);
        }
        el.scrollTop = hour * HOUR_PX;
    }, [dateIso, timed.length]);   // eslint-disable-line react-hooks/exhaustive-deps

    const txnCount = (activity?.expenses || []).length + (activity?.incomes || []).length;
    const dailyNet = activity?.net ?? 0;
    const currency = activity?.expense_currency || 'USD';
    const docCount = (activity?.documents_uploaded_list || []).length + (activity?.documents_edited_list || []).length;
    const usageDuration = activity?.usage_duration_ms || 0;

    return (
        <div className="h-full flex flex-col min-h-0">
            {/* Hourly grid — takes ALL leftover vertical space (flex-1) so the
                Finance/PAI module boxes lock to the bottom as a flush footer
                (#233 — was a fixed flex-[7] share that left a dead gap). */}
            <div className="flex-1 min-h-0 flex flex-col">
                {tbc.length > 0 && (
                    <div className="flex-shrink-0 px-3 py-1.5 border-b border-pai-amber/25 bg-pai-amber/[0.05]">
                        <div className="pai-eyebrow text-[10px] text-pai-amber mb-1">
                            {t('caleTimeTbc', TIME_TBC_LABEL)}
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {tbc.map((ev) => (
                                <button
                                    key={ev.id}
                                    data-testid="timeline-tbc-event"
                                    onClick={() => onOpenEvent(ev)}
                                    title={ev.title}
                                    className="text-[11px] px-2 py-0.5 rounded-full border border-pai-amber/40 bg-pai-amber/[0.08] text-pai-amber hover:bg-pai-amber/[0.16] truncate max-w-[160px] transition-colors"
                                >
                                    {ev.title}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div ref={scrollRef} data-testid="hourly-grid" className="flex-1 min-h-0 overflow-y-auto">
                    <div className="relative" style={{ height: 24 * HOUR_PX }}>
                        {/* Hour rows + labels */}
                        {Array.from({ length: 24 }).map((_, h) => (
                            <div
                                key={h}
                                data-testid="hour-row"
                                className="absolute left-0 right-0 border-t border-white/[0.055]"
                                style={{ top: h * HOUR_PX, height: HOUR_PX }}
                            >
                                <span className="absolute top-1.5 left-1.5 text-[13px] text-white/35 tabular-nums">
                                    {hourLabel(h)}
                                </span>
                            </div>
                        ))}
                        {/* Timed events mapped onto the grid, offset right of the labels. */}
                        <div className="absolute top-0 bottom-0 right-1" style={{ left: GRID_LABEL_GUTTER }}>
                            {timed.map((ev) => {
                                const { startMin, endMin } = eventMinutes(ev);
                                const pos = layout.get(ev.id) || { col: 0, cols: 1 };
                                const top = (startMin / 60) * HOUR_PX;
                                const height = Math.max(((endMin - startMin) / 60) * HOUR_PX, 18);
                                const widthPct = 100 / pos.cols;
                                const declined = ['declined', 'cancelled'].includes(eventDisplayStatus(ev));
                                const range = formatRange24(ev.start_time, ev.end_time);
                                // "Crunch" fix (#233): a block too short for the
                                // stacked title + time renders them on ONE line so
                                // the time is always visible ("Standup 09:00 - 09:20").
                                const inline = height < EVENT_STACK_MIN_PX;
                                return (
                                    <button
                                        key={ev.id}
                                        data-testid="timeline-event"
                                        onClick={() => onOpenEvent(ev)}
                                        title={`${ev.title} · ${formatDateRange(ev.start_time, ev.end_time)}`}
                                        style={{
                                            top,
                                            height,
                                            left: `${pos.col * widthPct}%`,
                                            width: `calc(${widthPct}% - 3px)`,
                                        }}
                                        // ⚠️ The block's height is set INLINE above and is
                                        // asserted (<=26px for a 20-minute event, #233), so
                                        // padding/type here must never push the box taller.
                                        className={`absolute overflow-hidden rounded-row border px-2 py-0.5 text-left transition-colors ${
                                            declined
                                                ? 'bg-white/[0.02] border-pai-hairline text-pai-faint'
                                                : 'bg-white/[0.05] border-pai text-white hover:bg-white/[0.1]'
                                        }`}
                                    >
                                        {inline ? (
                                            // Single line: title + time range, truncated.
                                            <span className={`block text-[11px] leading-tight truncate ${declined ? 'line-through' : ''}`}>
                                                <span className="font-bold">{ev.title}</span>
                                                {range && <span className="font-normal text-white/50 tnum">{` ${range}`}</span>}
                                            </span>
                                        ) : (
                                            <>
                                                <span className={`block text-[13px] font-bold leading-tight truncate ${declined ? 'line-through' : ''}`}>
                                                    {ev.title}
                                                </span>
                                                <span className="block text-[11px] leading-tight truncate text-white/50 tnum">
                                                    {range}
                                                </span>
                                            </>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Finance box — 10% */}
            <DayModuleBox
                Icon={Wallet}
                label={t('expenses', 'Finance')}
                sub={txnCount === 0
                    ? t('caleNoTransactionsLogged', 'No transactions logged')
                    : t('caleDayTxnSummary', '{n} transaction{s} · net {sign}{amount}', {
                        n: txnCount,
                        s: txnCount === 1 ? '' : 's',
                        sign: dailyNet >= 0 ? '+' : '−',
                        amount: formatAmount(Math.abs(dailyNet), currency),
                    })}
                value={txnCount > 0 ? txnCount : '·'}
                valueClass={dailyNet >= 0 ? 'text-pai-green' : 'text-pai-red'}
                accent={{
                    border: 'border-pai-hairline',
                    bg: 'bg-white/[0.03]',
                    iconBg: 'border-pai-amber/40 bg-pai-amber/[0.1]',
                    icon: 'text-pai-amber',
                }}
                testId="daydetail-finance-box"
                onClick={onPickFinance}
            />

            {/* PAI box — 10% */}
            <DayModuleBox
                Icon={FileText}
                label="PAI"
                sub={docCount === 0 && usageDuration === 0
                    ? t('caleNoPaiActivity', 'No PAI activity')
                    : t('caleDayDocsSummary', '{n} doc{s} · {duration} of usage', {
                        n: docCount,
                        s: docCount === 1 ? '' : 's',
                        duration: formatUsageDuration(usageDuration),
                    })}
                value={docCount > 0 ? docCount : (usageDuration > 0 ? formatUsageDuration(usageDuration) : '·')}
                valueClass="text-pai-blue"
                accent={{
                    border: 'border-pai-hairline',
                    bg: 'bg-white/[0.03]',
                    iconBg: 'border-pai-blue/40 bg-pai-blue/[0.1]',
                    icon: 'text-pai-blue',
                }}
                testId="daydetail-pai-box"
                onClick={onPickPai}
            />
        </div>
    );
};

// ── Events screen ──────────────────────────────────────────────────────
const EventsScreen = ({ events, onOpen, onNew }) => {
    const { t } = useLanguage();
    const pending = events.filter(isTimePending);
    const timed = events.filter((e) => !isTimePending(e));
    return (
        <div className="p-3 space-y-3">
            {pending.length > 0 && (
                <section>
                    <div className="pai-eyebrow text-[11px] text-pai-amber mb-1.5">
                        {t('caleTimeTbc', 'Time to be confirmed')}
                    </div>
                    <div className="space-y-1.5">
                        {pending.map((ev) => (
                            <EventListRow key={ev.id} event={ev} onClick={() => onOpen(ev)} />
                        ))}
                    </div>
                </section>
            )}
            {timed.length > 0 && (
                <section>
                    {pending.length > 0 && (
                        <div className="pai-eyebrow text-[11px] text-pai-green mb-1.5">
                            {t('caleWithTime', 'With time')}
                        </div>
                    )}
                    <div className="space-y-1.5">
                        {timed.map((ev) => (
                            <EventListRow key={ev.id} event={ev} onClick={() => onOpen(ev)} />
                        ))}
                    </div>
                </section>
            )}
            {events.length === 0 && (
                /* Empty state: centred, no illustration — a bold title over one
                   explanatory line (DESIGN_SYSTEM hard rule 6). */
                <div className="text-center py-10 px-6">
                    <p className="text-[18px] font-bold text-white/70">{t('caleNoEventsToday', 'No events on this day yet.')}</p>
                    <button
                        onClick={onNew}
                        className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-semibold text-white border border-pai bg-white/[0.05] hover:bg-white/[0.1] hover:border-pai-strong rounded-ctl transition-colors"
                    >
                        <Plus size={13} /> {t('caleAddEventBtn', 'Add event')}
                    </button>
                </div>
            )}
        </div>
    );
};

// Simple Event form (used for both new + edit). NO Date field — implicit
// from the day card we're already inside.
// ── Flexible time field (#271 Manual Event Creation) ────────────────────────
// A plain TEXT input + a dedicated AM/PM toggle, replacing <input type="time">
// whose rigid mask "blocked" the cursor (no free typing, no clean backspacing,
// browser-dependent segments). Parsing rules live in utils/timeInput.js.
//
// Two behaviours make it "intelligent" without ever fighting the typist:
//   • the TOGGLE flips live as soon as the typed value settles the meridiem on
//     its own — typing "22" moves it to PM immediately;
//   • the TEXT is normalised on BLUR (commit), so "22" becomes "10:00" once the
//     field is left. Rewriting on every keystroke would make "2230" impossible
//     to type, since "22" would already have become "10:00".
// Either way the SAVED value is taken from the parsed time, not the text, so a
// submit straight from the field records the right instant.
const TimeField = ({ id, label, text, meridiem, onTextChange, onMeridiemChange, disabled = false, testId }) => {
    const { t } = useLanguage();
    const handleChange = (raw) => {
        const parsed = parseTimeInput(raw, meridiem);
        if (parsed?.forced && parsed.meridiem !== meridiem) onMeridiemChange(parsed.meridiem);
        onTextChange(raw);
    };
    const handleBlur = () => {
        const norm = normalizeTimeText(text, meridiem);
        if (!norm) return;   // unparseable → keep what they typed; save complains
        onTextChange(norm.text);
        if (norm.meridiem !== meridiem) onMeridiemChange(norm.meridiem);
    };
    return (
        <div className="block">
            <label htmlFor={id} className="text-[11px] font-medium text-pai-meta">
                {label}
            </label>
            <div className="mt-1 flex items-stretch gap-1.5">
                <input
                    id={id}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    data-testid={testId}
                    value={text}
                    disabled={disabled}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    placeholder={t('caleTimeFieldPlaceholder', 'e.g. 9:30 or 22')}
                    className="pai-input flex-1 min-w-0 px-2.5 py-1.5 text-sm tnum disabled:opacity-50"
                />
                <div
                    data-testid={`${testId}-meridiem`}
                    data-value={meridiem}
                    role="group"
                    aria-label={t('caleAmPmAria', '{label} AM or PM', { label })}
                    className="flex-shrink-0 inline-flex rounded-seg border border-pai overflow-hidden"
                >
                    {[AM, PM].map((m) => (
                        <button
                            key={m}
                            type="button"
                            data-testid={`${testId}-${m.toLowerCase()}`}
                            aria-pressed={meridiem === m}
                            disabled={disabled}
                            onClick={() => onMeridiemChange(m)}
                            className={`px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
                                meridiem === m
                                    ? 'bg-white/[0.1] text-white'
                                    : 'text-pai-meta hover:bg-white/[0.06] hover:text-white'
                            }`}
                        >
                            {m}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Seed the time field from a stored event (edit mode).
const clockFromIso = (iso) => {
    if (!iso) return { text: '', meridiem: AM };
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { text: '', meridiem: AM };
    return { text: formatClock12(d.getHours(), d.getMinutes()), meridiem: meridiemOf(d.getHours()) };
};

// Manual event form — overhauled for #271:
//   • only "Event name" is mandatory;
//   • "Starting Time (optional)" (+ an equally optional end), both flexible;
//   • Location, People and Notes are three clearly separated fields (People was
//     previously smuggled into the notes textarea, so a guest was just prose —
//     it is now a live search over Network contacts + Teams that really invites);
//   • the action button becomes "Save and Invite" the moment anyone is added.
const EventForm = ({ event, dateIso, onSave, onCancel, saving }) => {
    const { t } = useLanguage();
    const [title, setTitle] = useState(event?.title || '');
    // Blank the time ONLY for a true date-only event (#269). Keying off midnight
    // alone silently wiped the time of a genuinely timed event that renders at
    // 00:00, and saving then nulled its end_time too.
    const initialStart = useMemo(
        () => (event?.start_time && !isTimePending(event) ? clockFromIso(event.start_time) : { text: '', meridiem: AM }),
        [event?.start_time, event?.end_time], // eslint-disable-line react-hooks/exhaustive-deps
    );
    const initialEnd = useMemo(() => clockFromIso(event?.end_time), [event?.end_time]);
    const [startText, setStartText] = useState(initialStart.text);
    const [startMeridiem, setStartMeridiem] = useState(initialStart.meridiem);
    const [endText, setEndText] = useState(initialEnd.text);
    const [endMeridiem, setEndMeridiem] = useState(initialEnd.meridiem);
    const [location, setLocation] = useState(event?.location || '');
    const [notes, setNotes] = useState(event?.description || '');
    const [people, setPeople] = useState([]);
    const [error, setError] = useState(null);

    const handleSave = (e) => {
        e?.preventDefault();
        if (!title.trim()) { setError(t('caleEventNameRequired', 'Event name is required.')); return; }

        const start = startText.trim() ? parseTimeInput(startText, startMeridiem) : null;
        if (startText.trim() && !start) {
            setError(t('caleStartTimeInvalid', 'Starting time not recognised — try 9:30, 930 or 22.'));
            return;
        }
        const end = (start && endText.trim()) ? parseTimeInput(endText, endMeridiem) : null;
        if (start && endText.trim() && !end) {
            setError(t('caleEndTimeInvalid', 'End time not recognised — try 10:30, 1030 or 23.'));
            return;
        }

        // Serialize via a Date built from LOCAL components so the BE stores the
        // correct UTC instant whatever the user's timezone.
        //   • timed event   → local (date + time)
        //   • untimed event → local midnight of dateIso, no end — the canonical
        //     "Time to be Confirmed" sentinel (#269).
        const [y, mo, d] = dateIso.split('-').map(Number);
        const buildLocal = (h, mi) => new Date(y, (mo || 1) - 1, d || 1, h || 0, mi || 0, 0, 0);
        const startDate = start ? buildLocal(start.hour24, start.minute) : buildLocal(0, 0);
        let endDate = end ? buildLocal(end.hour24, end.minute) : null;
        // A start of exactly local midnight with no end would be indistinguishable
        // from a date-only placeholder, so record the chosen clock time by giving
        // it the standard 1h duration — the same default every other timed create
        // path uses (create_calendar_event, POST /events/with-invitations). Only
        // this ambiguous case is defaulted; every other start-only event keeps a
        // null end exactly as before.
        if (start && !endDate && start.hour24 === 0 && start.minute === 0) {
            endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
        }

        onSave({
            title: title.trim(),
            start_time: startDate.toISOString(),
            end_time: endDate ? endDate.toISOString() : null,
            location: location.trim() || null,
            description: notes.trim() || null,
        }, people);
    };

    const hasPeople = people.length > 0;

    return (
        <form data-testid="event-form" onSubmit={handleSave} className="p-3 space-y-3">
            <div className="block">
                <label htmlFor="event-name-field" className="text-[11px] font-medium text-pai-meta">
                    {t('caleEventNameLabel', 'Event name *')}
                </label>
                <input
                    id="event-name-field"
                    data-testid="event-name-input"
                    type="text"
                    value={title}
                    autoFocus
                    onChange={(e) => setTitle(e.target.value)}
                    className="pai-input mt-1 w-full px-2.5 py-2 text-sm"
                    required
                />
            </div>

            <TimeField
                id="event-start-time-field"
                testId="event-time-input"
                label={t('caleStartTimeLabel', 'Starting Time (optional)')}
                text={startText}
                meridiem={startMeridiem}
                onTextChange={setStartText}
                onMeridiemChange={setStartMeridiem}
            />
            <TimeField
                id="event-end-time-field"
                testId="event-end-time-input"
                label={t('caleEndTimeLabel', 'End Time (optional)')}
                text={endText}
                meridiem={endMeridiem}
                onTextChange={setEndText}
                onMeridiemChange={setEndMeridiem}
                disabled={!startText.trim()}
            />

            <div className="block">
                <label htmlFor="event-location-field" className="text-[11px] font-medium text-pai-meta">
                    {t('commonLocation', 'Location')}
                </label>
                <input
                    id="event-location-field"
                    data-testid="event-location-input"
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder={t('caleLocationPlaceholder', 'e.g. Café Luna, Zoom, the office')}
                    className="pai-input mt-1 w-full px-2.5 py-2 text-sm"
                />
            </div>

            <div className="block">
                <span className="text-[11px] font-medium text-pai-meta">{t('calePeopleLabel', 'People')}</span>
                <div className="mt-1">
                    <PeoplePicker
                        testId="event-people"
                        selected={people}
                        onSelect={(entry) => { setError(null); setPeople((prev) => [...prev, entry]); }}
                        onRemove={(entry) => setPeople((prev) => prev.filter((p) => p.key !== entry.key))}
                        placeholder={t('caleInvitePlaceholder', 'Invite a contact or team…')}
                    />
                </div>
            </div>

            <div className="block">
                <label htmlFor="event-notes-field" className="text-[11px] font-medium text-pai-meta">
                    {t('caleNotesHeading', 'Notes')}
                </label>
                <textarea
                    id="event-notes-field"
                    data-testid="event-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder={t('caleNotesPlaceholder', "What it's about")}
                    className="pai-input mt-1 w-full px-2.5 py-2 text-sm resize-none"
                />
            </div>

            {!startText.trim() && (
                <p className="text-[11px] text-pai-amber">
                    {t('caleNoTimeSetWarning', 'No time set — this event will land in “Time to be confirmed” for the day.')}
                </p>
            )}
            {error && <div className="text-xs text-pai-red">{error}</div>}
            <div className="flex flex-wrap justify-end gap-2 pt-1">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={saving}
                    className="px-3.5 py-2 text-sm font-semibold text-pai-secondary hover:text-white hover:bg-white/[0.07] rounded-ctl transition-colors"
                >
                    {t('commonCancel', 'Cancel')}
                </button>
                <button
                    type="submit"
                    data-testid="event-save-button"
                    disabled={saving}
                    className="px-4 py-2 text-sm font-semibold text-pai-void bg-white hover:bg-white/[0.88] disabled:opacity-50 rounded-ctl transition-colors"
                >
                    {saving ? t('commonSaving', 'Saving…') : (hasPeople ? t('caleSaveAndInvite', 'Save and Invite') : t('commonSave', 'Save'))}
                </button>
            </div>
        </form>
    );
};

// Read-only event detail (the screen the user lands on when tapping a row).
// Verified invitation dialog (Agenda V2 §4) — pick a linked contact,
// confirm "I found X. Send the invitation?", fire calendarService.inviteToEvent.
const InviteContactDialog = ({ open, event, onClose }) => {
    const { t } = useLanguage();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(() => new Set()); // contact ids staged for this batch
    const [sending, setSending] = useState(false);
    const [sentTo, setSentTo] = useState(() => new Set());     // already invited from this dialog
    const [resultMsg, setResultMsg] = useState(null);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setQuery('');
        setSelected(new Set());
        setSentTo(new Set());
        setResultMsg(null);
        contactService.listContacts({})
            .then((res) => {
                if (cancelled) return;
                const all = res?.contacts || [];
                // STRICTLY the user's Network: contacts with a completed mutual
                // handshake (tier==='network', backed by a ContactLink — NOT a bare
                // omnis_user_id). This eliminates the "leakage" of unrelated / merely
                // linked contacts from the invite list (Manual Invite Menu ticket).
                setContacts(all.filter((c) => c.tier === 'network' && c.omnis_user_id));
            })
            .catch((err) => { if (!cancelled) setError(userErrorMessage(err, t)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see note above: 't' is intentionally omitted
    }, [open]);

    if (!open) return null;

    // Real-time, as-you-type filtering of the already-loaded Network list — no
    // Enter, no round-trip (the list "rolls up" on every keystroke).
    const q = query.trim().toLowerCase();
    const filtered = q
        ? contacts.filter((c) => (
            `${c.name || ''} ${c.omnis_user?.email || ''} ${c.email || ''}`.toLowerCase().includes(q)
        ))
        : contacts;

    const toggle = (id) => {
        if (sentTo.has(id)) return; // can't re-select an already-invited guest
        setResultMsg(null);
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // The staged recipients (exclude any already invited this session).
    const staged = contacts.filter((c) => selected.has(c.id) && !sentTo.has(c.id));

    const sendBatch = async () => {
        if (!staged.length || sending) return;
        setSending(true);
        setError(null);
        setResultMsg(null);
        const ok = [];
        const failed = [];
        // One invitation per recipient — a single Send dispatches the whole batch.
        for (const c of staged) {
            try {
                await calendarService.inviteToEvent(event.id, { invitee_user_id: c.omnis_user_id });
                ok.push(c.id);
            } catch (_) {
                failed.push(c.name || 'contact');
            }
        }
        if (ok.length) {
            setSentTo((prev) => { const n = new Set(prev); ok.forEach((id) => n.add(id)); return n; });
            setSelected(new Set());
            // Refresh the calendar so the open EventDetailView immediately shows the
            // new guest(s) (status + per-guest "Uninvite" button) without a reload
            // (Reload Invite bug #56). Mirrors the revoke flow.
            window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated'));
        }
        if (failed.length) {
            setError(t('caleCouldntInviteList', "Couldn't invite: {names}", { names: failed.join(', ') }));
        } else {
            setResultMsg(t('caleInvitationsSentTo', 'Invitation{s} sent to {n} {person}.', {
                s: ok.length > 1 ? 's' : '',
                n: ok.length,
                person: ok.length > 1 ? t('calePeoplePlural', 'people') : t('calePersonSingular', 'person'),
            }));
        }
        setSending(false);
    };

    const sendLabel = staged.length > 1
        ? t('caleSendNInvites', 'Send {n} invites', { n: staged.length })
        : t('caleSendInvite', 'Send invite');

    return (
        /* The backdrop's `fixed inset-0 z-[80]` triplet is a SELECTOR in
           reload-invite.spec.js (a click-outside at 8,8) — keep zIndex literal. */
        <Modal onClose={onClose} zIndex="z-[80]" layout="column" cardClassName="max-h-[80dvh]" cardTestId="invite-dialog">
                <div className="flex items-center justify-between p-5 border-b border-pai-hairline">
                    <h3 className="text-[17px] font-bold text-white flex items-center gap-2">
                        <UserPlus size={16} className="text-pai-blue" />
                        {t('caleInviteToEvent', 'Invite to “{title}”', { title: event.title || t('caleEventWord', 'event') })}
                    </h3>
                    <button onClick={onClose} className="pai-icon-btn w-[38px] h-[38px]">
                        <X size={16} />
                    </button>
                </div>

                {/* Search bar — filters The Network in real time as the user types. */}
                {!loading && contacts.length > 0 && (
                    <div className="px-5 pt-4">
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-pai-meta pointer-events-none" />
                            <input
                                data-testid="invite-search"
                                type="text"
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={t('caleSearchYourNetwork', 'Search your network…')}
                                className="pai-input w-full pl-9 pr-3 py-2 text-sm"
                            />
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-5 pt-4 space-y-3">
                    {error && (
                        <div className="bg-pai-red/[0.08] border border-pai-red/30 rounded-row p-2.5 text-[13px] text-pai-red">
                            {error}
                        </div>
                    )}
                    {resultMsg && !error && (
                        <div data-testid="invite-result" className="bg-pai-green/[0.08] border border-pai-green/30 rounded-row p-2.5 text-[13px] text-pai-green">
                            {resultMsg}
                        </div>
                    )}
                    {loading ? (
                        <p className="text-sm text-pai-meta">{t('caleLoadingYourNetwork', 'Loading your network…')}</p>
                    ) : contacts.length === 0 ? (
                        <p className="text-sm text-pai-meta">
                            {t('caleNetworkEmptyLong', 'Your Network is empty. Connect with someone first — open Agenda → add them by email → once they accept the handshake they’ll appear here to invite.')}
                        </p>
                    ) : filtered.length === 0 ? (
                        <p className="text-sm text-pai-meta">{t('caleNoNetworkMatch', 'No one in your Network matches “{query}”.', { query })}</p>
                    ) : (
                        <ul className="space-y-1">
                            {filtered.map((c) => {
                                const wasSent = sentTo.has(c.id);
                                const isSel = selected.has(c.id);
                                return (
                                    <li key={c.id}>
                                        <button
                                            data-testid="invite-contact-row"
                                            aria-pressed={isSel}
                                            disabled={wasSent}
                                            onClick={() => toggle(c.id)}
                                            className={`w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-row border transition-colors ${
                                                wasSent
                                                    ? 'bg-pai-green/[0.08] border-pai-green/30 text-pai-green cursor-not-allowed'
                                                    : isSel
                                                        ? 'bg-white/[0.055] border-white/80 shadow-glow-sm text-white'
                                                        : 'border-transparent text-pai-body hover:bg-white/[0.07]'
                                            }`}
                                        >
                                            {/* Selection checkbox */}
                                            <span className={`w-4 h-4 rounded-chip border flex items-center justify-center flex-shrink-0 ${
                                                (isSel || wasSent)
                                                    ? 'bg-white border-white text-pai-void'
                                                    : 'border-pai-strong'
                                            }`}>
                                                {(isSel || wasSent) && <Check size={11} />}
                                            </span>
                                            <div className="w-7 h-7 rounded-full bg-white text-pai-void flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                                {(c.name || '?').split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate">{c.name}</div>
                                                <div className="text-[12.5px] text-pai-meta truncate">
                                                    {c.omnis_user?.email || c.email || ''}
                                                </div>
                                            </div>
                                            {wasSent && <span className="text-[10px] font-semibold">{t('caleInvitedBadge', 'Invited')}</span>}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Single Send button dispatches the whole selection (1 or many) at once. */}
                {staged.length > 0 && (
                    <div className="p-5 border-t border-pai-hairline flex items-center justify-between gap-2">
                        <span className="text-[13px] text-pai-meta tnum">
                            {t('caleSelectedCount', '{n} selected', { n: staged.length })}
                        </span>
                        <button
                            data-testid="invite-send"
                            onClick={sendBatch}
                            disabled={sending}
                            className="px-4 py-2 text-[13px] font-semibold text-pai-void bg-white hover:bg-white/[0.88] disabled:opacity-50 rounded-ctl inline-flex items-center gap-1.5 transition-colors"
                        >
                            <Send size={11} /> {sending ? t('caleSending', 'Sending…') : sendLabel}
                        </button>
                    </div>
                )}
            </Modal>
    );
};

const EventDetailView = ({ event, onDelete }) => {
    const { t } = useLanguage();
    const pending = isTimePending(event);
    const [showInvite, setShowInvite] = useState(false);
    // Optimistically drop a guest while their revoke is in flight (Remove Event
    // Invitation card).
    const [revokingId, setRevokingId] = useState(null);
    const [revokedIds, setRevokedIds] = useState([]);

    const handleRevoke = async (inv) => {
        if (!inv?.id || revokingId) return;
        setRevokingId(inv.id);
        try {
            await calendarService.revokeInvitation(inv.id);
            setRevokedIds((prev) => [...prev, inv.id]);
            // Refresh the calendar so the synced status + dual-write cleanup show.
            window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated'));
        } catch (_) {
            // leave the guest in place so the organizer can retry
        } finally {
            setRevokingId(null);
        }
    };

    const visibleInvitations = (event.invitations || []).filter((i) => !revokedIds.includes(i.id));
    return (
        <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
                {/* Stays a real heading: hourly-grid / calendar-conflict-tbc look it
                    up with getByRole('heading', { name: title }). */}
                <h4 className="text-[19px] font-bold tracking-[-0.015em] text-white">{event.title}</h4>
                <StatusBadge status={eventDisplayStatus(event)} />
                    <SeriesBadge event={event} />
                <AttendanceBadge attendance={event.attendance} invitations={event.invitations} />
            </div>
            {pending ? (
                <div className="flex items-center gap-2 text-[13px] text-pai-amber bg-pai-amber/[0.05] border border-pai-amber/30 rounded-row px-2.5 py-2">
                    <Clock size={14} className="text-pai-amber flex-shrink-0" />
                    {t('caleTimeTbcEditHint', '{label} — set a time via Edit.', { label: t('caleTimeTbc', TIME_TBC_LABEL) })}
                </div>
            ) : (
                <div data-testid="event-detail-time" className="flex items-center gap-2 text-[15px] text-pai-body tnum">
                    <Clock size={14} className="text-pai-meta" />
                    {formatDateRange(event.start_time, event.end_time)}
                </div>
            )}
            {event.location && (
                <div className="flex items-start gap-2 text-[15px] text-pai-body">
                    <MapPin size={14} className="text-pai-meta mt-0.5" />
                    <span>{event.location}</span>
                </div>
            )}
            {event.description && (
                <div className="text-[15px] leading-relaxed text-pai-secondary whitespace-pre-wrap border-t border-pai-hairline pt-3">
                    {event.description}
                </div>
            )}
            <AttendanceBreakdown invitations={visibleInvitations} onRevoke={handleRevoke} revokingId={revokingId} />
            <div className="border-t border-pai-hairline pt-3 flex items-center justify-between flex-wrap gap-2">
                <button
                    onClick={() => setShowInvite(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold text-white border border-pai bg-white/[0.05] hover:bg-white/[0.1] hover:border-pai-strong rounded-ctl transition-colors"
                >
                    <UserPlus size={12} /> {t('caleInviteContact', 'Invite contact')}
                </button>
                <button
                    onClick={onDelete}
                    className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-pai-red hover:underline"
                >
                    <Trash2 size={12} /> {t('caleDeleteEvent', 'Delete event')}
                </button>
            </div>
            <InviteContactDialog open={showInvite} event={event} onClose={() => setShowInvite(false)} />
        </div>
    );
};

// ── Finance screen ─────────────────────────────────────────────────────
// Calendar Financial Ticket — §1 Multi-Book Daily Activity Segmentation +
// §2 Financial Deep Link. When a single day has activity across several books,
// aggregating everything into one "Daily Net" is misleading, so we first force
// a per-book selection (alphabetical, centered boxes), then show that one
// book's daily breakdown. The book-name header inside the breakdown deep-links
// straight to that book's full ledger in the Finance module.
const FinanceScreen = ({ activity, onOpenExpense, onOpenBookLedger }) => {
    const { t } = useLanguage();
    const currency = activity?.expense_currency || 'USD';

    // Segment the day's rows by book (book_id === null → "Unassigned").
    const books = useMemo(() => {
        const expenseList = activity?.expenses || [];
        const incomeList = activity?.incomes || [];
        const map = new Map();
        const add = (row, kind) => {
            const key = row.book_id || '__unassigned';
            const name = row.book_name || t('caleUnassignedBook', 'Unassigned');
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    bookKey: row.book_id || 'unassigned', // deep-link param for ExpensesPanel
                    name,
                    expenses: [],
                    incomes: [],
                });
            }
            map.get(key)[kind].push(row);
        };
        expenseList.forEach((r) => add(r, 'expenses'));
        incomeList.forEach((r) => add(r, 'incomes'));
        const out = Array.from(map.values());
        // Sub-total the CONVERTED value, never the raw amount (#196/#250).
        // This used to `reduce` over `r.amount`, which sums each row in whatever
        // currency it was captured in and then renders the result with ONE
        // symbol — a THB 1,000 + EUR 100 day showed "€1,100.00". The server now
        // sends `converted_amount` per row (already expressed in
        // `activity.expense_currency`, the same unit the day totals use); a row it
        // could not price comes through as null and is left out of the sub-total,
        // exactly as the server leaves it out of the day total.
        const sumConverted = (rows) => rows.reduce(
            (s, r) => s + (Number.isFinite(Number(r.converted_amount))
                ? Number(r.converted_amount) : 0),
            0,
        );
        for (const b of out) {
            b.expenseTotal = sumConverted(b.expenses);
            b.incomeTotal = sumConverted(b.incomes);
            b.net = b.incomeTotal - b.expenseTotal;
            b.unconverted = [...b.expenses, ...b.incomes].filter(
                (r) => r.amount != null && r.converted_amount == null).length;
        }
        // Alphabetical by book name (case-insensitive, locale-aware).
        out.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        return out;
    }, [activity?.expenses, activity?.incomes, t]);

    // A single book auto-selects; multiple books force an explicit choice.
    const [selectedKey, setSelectedKey] = useState(null);
    const effectiveKey = books.length === 1 ? books[0].key : selectedKey;
    const selectedBook = books.find((b) => b.key === effectiveKey) || null;

    const renderTxnRow = (exp) => (
        <button
            key={exp.id}
            onClick={() => onOpenExpense(exp)}
            /* The Finance semantic row tinting: a 1px accent border over a <=12%
               wash, deepening on hover. Never an accent-filled row. */
            className={`w-full text-left rounded-row border p-2.5 transition-colors ${
                exp.direction === 'income'
                    ? 'border-pai-green/30 bg-pai-green/[0.04] hover:bg-pai-green/[0.09]'
                    : 'border-pai-red/30 bg-pai-red/[0.04] hover:bg-pai-red/[0.09]'
            }`}
        >
            <div className="flex items-baseline gap-2">
                <span className={`text-sm font-extrabold tnum ${
                    exp.direction === 'income' ? 'text-pai-green' : 'text-white'
                }`}>
                    {exp.direction === 'income' ? '+' : ''}
                    {formatAmount(exp.amount, exp.currency)}
                </span>
                <span className="text-[13px] text-pai-secondary truncate">
                    {exp.merchant || t('caleUnknownMerchant', 'Unknown')}
                </span>
            </div>
            {exp.category && (
                <div className="mt-0.5 text-[11px] text-pai-meta">
                    {exp.category}
                </div>
            )}
        </button>
    );

    const renderColumn = (rows, label, accentColor) => (
        <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-baseline justify-between mb-1">
                <span className={`pai-eyebrow text-[10px] ${accentColor}`}>
                    {label}
                </span>
            </div>
            {rows.length === 0 && (
                <p className="text-[12.5px] text-pai-faint italic">{t('caleNothingInColumn', 'Nothing in this column.')}</p>
            )}
            <div className="space-y-1">
                {rows.map(renderTxnRow)}
            </div>
        </div>
    );

    if (books.length === 0) {
        return (
            /* Empty state: centred, no illustration (DESIGN_SYSTEM hard rule 6). */
            <div className="px-6 py-[34px] text-center">
                <p className="text-[18px] font-bold text-white/70">{t('caleNoTransactionsLoggedDay', 'No transactions logged on this day.')}</p>
            </div>
        );
    }

    // ── Book picker (multiple books, none chosen yet) ───────────────────
    if (!selectedBook) {
        return (
            <div className="p-3 space-y-2">
                <div className="pai-eyebrow text-[11px]">
                    {t('caleMultipleBooksPickOne', 'Multiple books have activity today — pick one')}
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {books.map((b) => (
                        <button
                            key={b.key}
                            onClick={() => setSelectedKey(b.key)}
                            className="rounded-card border border-pai bg-white/[0.03] p-4 flex flex-col items-center justify-center text-center hover:bg-white/[0.08] hover:border-pai-strong transition-colors min-h-[84px]"
                        >
                            <span className="text-[15px] font-semibold text-white">{b.name}</span>
                            <span className={`text-[13px] mt-1 tnum ${b.net >= 0 ? 'text-pai-green' : 'text-pai-red'}`}>
                                {t('caleNetAmount', 'net {sign}{amount}', { sign: b.net >= 0 ? '+' : '−', amount: formatAmount(Math.abs(b.net), currency) })}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    // ── Single book's daily breakdown ───────────────────────────────────
    const net = selectedBook.net;
    return (
        <div className="p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
                {books.length > 1 ? (
                    <button
                        onClick={() => setSelectedKey(null)}
                        className="inline-flex items-center gap-1 text-[13px] text-pai-meta hover:text-white transition-colors"
                    >
                        <ChevronLeft size={14} /> {t('caleAllBooks', 'All books')}
                    </button>
                ) : <span />}
                {/* §2 Book name — STATIC label (Calendar finance double ticket).
                    The redundant header deep-link was removed; the ONLY bridge to
                    the Finance module is the "Open … in Expenses" link below. */}
                {/* Stays a <span>: calendar-finance-header.spec.js asserts the tag
                    name and that clicking it is inert. */}
                <span
                    data-testid="finance-book-name"
                    title={selectedBook.name}
                    className="text-[15px] font-semibold text-pai-amber"
                >
                    {selectedBook.name}
                </span>
            </div>

            {/* Net out header (scoped to THIS book) */}
            <div className={`rounded-card p-4 text-center border ${
                net >= 0
                    ? 'bg-pai-green/[0.05] border-pai-green/30'
                    : 'bg-pai-red/[0.05] border-pai-red/30'
            }`}>
                <div className="pai-eyebrow text-[10px]">
                    {t('caleDailyNetResult', 'Daily Net Result')}
                </div>
                <div className={`text-[28px] font-extrabold tracking-[-0.03em] tnum mt-1 ${
                    net >= 0 ? 'text-pai-green' : 'text-pai-red'
                }`}>
                    {net >= 0 ? '+' : '−'}{formatAmount(Math.abs(net), currency)}
                </div>
                <div className="text-[12.5px] text-pai-meta mt-1 tnum">
                    <span className="text-pai-green">{formatAmount(selectedBook.incomeTotal, currency)}</span>
                    {' − '}
                    <span className="text-pai-red">{formatAmount(selectedBook.expenseTotal, currency)}</span>
                </div>
            </div>

            <div className="flex gap-2 items-start">
                {renderColumn(selectedBook.expenses, t('caleExpensesCount', 'Expenses ({n})', { n: selectedBook.expenses.length }), 'text-pai-red')}
                {renderColumn(selectedBook.incomes, t('caleIncomeCount', 'Income ({n})', { n: selectedBook.incomes.length }), 'text-pai-green')}
            </div>

            <button
                onClick={() => onOpenBookLedger(selectedBook.bookKey)}
                className="w-full text-center text-[13px] font-semibold text-pai-amber hover:underline"
            >
                {t('caleOpenBookInFinance', 'Open {name} in the Finance module →', { name: selectedBook.name })}
            </button>
        </div>
    );
};

// ── Omnis productivity screen ──────────────────────────────────────────
const OmnisScreen = ({ activity, onPreviewDocument, onDownloadDocument, onRenameDocument }) => {
    const { t } = useLanguage();
    const docsUploaded = activity?.documents_uploaded_list || [];
    const docsEdited = activity?.documents_edited_list || [];
    const totalDocs = docsUploaded.length + docsEdited.length;
    const usageDuration = activity?.usage_duration_ms || 0;

    return (
        <div className="p-3 space-y-3">
            {/* Read-only stats. Usage TIME only (#424): the old "Credits" tile summed
                the day's raw LLM token counts (the ~38K fixed prefix is re-sent on
                every agentic call, so it read in the millions) — Energy lives in the
                Energy Tank, not here. */}
            <div
                data-testid="daydetail-usage-time"
                className="rounded-card border border-pai-blue/25 bg-pai-blue/[0.04] p-4"
            >
                <div className="pai-eyebrow text-[10px] text-pai-blue">{t('caleUsageTime', 'Usage time')}</div>
                <div className="text-[22px] font-extrabold tracking-[-0.02em] text-white mt-1 tnum">
                    {formatUsageDuration(usageDuration)}
                </div>
            </div>

            {/* Document list */}
            <div>
                <div className="pai-eyebrow text-[11px] mb-1.5">
                    {t('caleDocumentsTouched', 'Documents touched · {n}', { n: totalDocs })}
                </div>
                {totalDocs === 0 ? (
                    <p className="text-[13px] text-pai-meta">{t('caleNoDocsTouched', 'No documents created or edited on this day.')}</p>
                ) : (
                    <div className="space-y-1.5">
                        {docsUploaded.map((doc) => (
                            <DocumentRow
                                key={doc.id}
                                doc={doc}
                                onPreview={onPreviewDocument}
                                onDownload={onDownloadDocument}
                                onRename={onRenameDocument}
                            />
                        ))}
                        {docsEdited.map((doc) => (
                            <DocumentRow
                                key={doc.id}
                                doc={doc}
                                onPreview={onPreviewDocument}
                                onDownload={onDownloadDocument}
                                onRename={onRenameDocument}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ── Day-detail "back of card" navigator ─────────────────────────────────
const DayDetailCard = ({
    dateLabel,
    dateIso,
    activity,
    onClose,
    onUpdateEvent,
    onDeleteEvent,
    onCreateEvent,
    onInviteToEvent,
    onOpenExpense,
    onPreviewDocument,
    onDownloadDocument,
    onRenameDocument,
    onPrevDay,
    onNextDay,
    onBusyChange,
    onDeepLinkConsumed,
    initialScreen = 'menu',
    initialEventId = null,
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [screen, setScreen] = useState(initialScreen);
    const [activeEvent, setActiveEvent] = useState(null);
    const [savingEvent, setSavingEvent] = useState(false);

    // Tell the parent when an edit/create form (or a save) is in progress, so its
    // background poll pauses activity refetches and can't clobber the edit (#245).
    useEffect(() => {
        onBusyChange?.(screen === 'event_form' || savingEvent);
    }, [screen, savingEvent, onBusyChange]);
    // Always release the busy lock when this day card unmounts (day closed).
    useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

    // When the parent flips this card open with a deep-link event id, jump
    // straight to the event-edit form (used by the 24h-alert deep link).
    //
    // 🛑 The deep-link is a ONE-SHOT COMMAND, not a persistent prop (TBC: UI
    // persistence #337). This effect also depends on `activity.events`, because
    // the events for the day arrive after the first render — but that means it
    // re-fired on EVERY later identity change of that array too, and saving the
    // form causes exactly one: handleSaveEvent → onUpdateEvent → PATCH → await
    // fetchActivity() → new activityByDate. The awaited continuation's
    // setScreen('event_detail') is scheduled BEFORE React commits that render,
    // so the effect ran last and flipped the screen straight back to
    // 'event_form' — the edit interface "failed to dismiss" after Save and only
    // Cancel (which triggers no refetch) got the user out. Manual create/edit
    // was unaffected only because it carries no initialEventId. The ref makes
    // the jump happen exactly once per id; `onDeepLinkConsumed` then clears the
    // parent's state so re-opening the day later doesn't re-open the form.
    const consumedDeepLinkRef = useRef(null);
    useEffect(() => {
        if (!initialEventId || !activity?.events) return;
        if (consumedDeepLinkRef.current === initialEventId) return;
        const match = activity.events.find((e) => e.id === initialEventId);
        if (!match) return;   // not loaded yet — stay armed, retry on the next activity
        consumedDeepLinkRef.current = initialEventId;
        setActiveEvent(match);
        setScreen('event_form');
        onDeepLinkConsumed?.();
    }, [initialEventId, activity?.events, onDeepLinkConsumed]);

    // Keep the activeEvent in sync if the upstream activity refetch changed it.
    // Keyed on activeEvent?.id (not the whole object): the effect calls
    // setActiveEvent(fresh), so depending on the full `activeEvent` object would
    // re-trigger itself every time and loop.
    useEffect(() => {
        if (!activeEvent) return;
        const fresh = (activity?.events || []).find((e) => e.id === activeEvent.id);
        if (fresh) setActiveEvent(fresh);
    }, [activity?.events, activeEvent?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const events = activity?.events || [];

    // "Save and Invite" (#271): the form's People field returns a selection of
    // Network contacts / Teams, which is dispatched as real invitations right
    // after the event is saved. Invitations are sent through the SAME endpoint
    // the manual Invite dialog uses, so the event itself is still created by the
    // ordinary create path — which is what keeps the double-booking guard (#268)
    // and, critically, the date-only TBC shape intact (POST /events/with-invitations
    // always stamps a 1h end, which would silently turn an untimed event into a
    // timed one).
    const handleSaveEvent = async (payload, invitees = []) => {
        setSavingEvent(true);
        try {
            if (activeEvent) {
                await onUpdateEvent(activeEvent.id, payload);
                if (invitees.length) await onInviteToEvent?.(activeEvent.id, invitees);
                setScreen('event_detail');
            } else {
                const created = await onCreateEvent(payload);
                if (created) setActiveEvent(created);
                if (created && invitees.length) {
                    await onInviteToEvent?.(created.id, invitees);
                    // Land on the event's detail so the organizer immediately sees
                    // the guest list they just invited.
                    setScreen('event_detail');
                } else {
                    setScreen('events');
                }
            }
        } finally {
            setSavingEvent(false);
        }
    };

    const handleDeleteCurrentEvent = async () => {
        if (!activeEvent) return;
        await onDeleteEvent(activeEvent.id);
        setActiveEvent(null);
        setScreen('events');
    };

    // Universal Back: one level up the hierarchy.
    const goBack = () => {
        if (screen === 'event_detail') { setScreen('events'); return; }
        if (screen === 'event_form') {
            // Edit-form: back to detail. New-form: back to list.
            setScreen(activeEvent ? 'event_detail' : 'events');
            return;
        }
        if (screen === 'events' || screen === 'finance' || screen === 'omnis') {
            setScreen('menu');
            return;
        }
    };

    let title = dateLabel;
    let subtitle = null;
    let backHandler = goBack;
    let rightSlot = null;

    if (screen === 'menu') {
        backHandler = null;  // X handles the only exit from the menu
        const dailyNet = activity?.net ?? 0;
        const currency = activity?.expense_currency || 'USD';
        const hasAnyTxn = (activity?.expenses?.length || 0) + (activity?.incomes?.length || 0) > 0;
        if (hasAnyTxn) {
            subtitle = (
                <span className={`tnum ${dailyNet >= 0 ? 'text-pai-green' : 'text-pai-red'}`}>
                    {t('caleNetLabel', 'Net {sign}{amount}', { sign: dailyNet >= 0 ? '+' : '−', amount: formatAmount(Math.abs(dailyNet), currency) })}
                </span>
            );
        }
        // Prominent green "Add Event" button in the header — between the
        // date/navigation arrows and the X close (Hourly grid #176).
        rightSlot = (
            <button
                data-testid="day-detail-add-event"
                onClick={() => { setActiveEvent(null); setScreen('event_form'); }}
                // Deliberately shorter than the handoff's 40px "Add Event": this
                // header sits above the hourly grid, whose visible span is asserted
                // at >=11 hours (day-fixes-v2), so header height is a hard budget.
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white border border-pai bg-white/[0.05] hover:bg-white/[0.1] hover:border-pai-strong rounded-ctl transition-colors"
                title={t('caleAddEventTooltip', 'Add event')}
            >
                <Plus size={13} /> {t('caleAddEventBtnCaps', 'Add Event')}
            </button>
        );
    } else if (screen === 'events') {
        title = t('caleEventsForDay', 'Events — {date}', { date: dateLabel });
        rightSlot = (
            <button
                onClick={() => { setActiveEvent(null); setScreen('event_form'); }}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white border border-pai bg-white/[0.05] hover:bg-white/[0.1] hover:border-pai-strong rounded-ctl transition-colors"
                title={t('caleAddEventTooltip', 'Add event')}
            >
                <Plus size={12} /> {t('caleNewBtn', 'New')}
            </button>
        );
    } else if (screen === 'event_detail') {
        title = activeEvent?.title || t('caleEventFallbackCaps', 'Event');
        subtitle = dateLabel;
        rightSlot = (
            <button
                onClick={() => setScreen('event_form')}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white border border-pai bg-white/[0.05] hover:bg-white/[0.1] hover:border-pai-strong rounded-ctl transition-colors"
                title={t('caleEditEventTooltip', 'Edit event')}
            >
                <Pencil size={12} /> {t('commonEdit', 'Edit')}
            </button>
        );
    } else if (screen === 'event_form') {
        title = activeEvent ? t('caleEditingTitle', 'Editing — {title}', { title: activeEvent.title }) : t('caleNewEventTitle', 'New event');
        subtitle = dateLabel;
    } else if (screen === 'finance') {
        title = t('caleFinanceForDay', 'Finance — {date}', { date: dateLabel });
    } else if (screen === 'omnis') {
        title = t('calePaiForDay', 'PAI — {date}', { date: dateLabel });
    }

    return (
        <div data-testid="day-detail-container" className="h-full flex flex-col">
            <ScreenHeader
                title={title}
                subtitle={typeof subtitle === 'string' ? subtitle : (subtitle || null)}
                onBack={backHandler}
                onClose={onClose}
                right={rightSlot}
                onPrevDay={screen === 'menu' ? onPrevDay : null}
                onNextDay={screen === 'menu' ? onNextDay : null}
            />
            {/* ⚠️ The literal `overflow-y-auto` token is how day-fixes-v2 locates
                this scroll ancestor (`dd.closest('.overflow-y-auto')`). Keep it. */}
            <div className={`flex-1 min-h-0 ${screen === 'menu' ? 'overflow-hidden' : 'overflow-y-auto'}`}>
                {screen === 'menu' && (
                    <DayTimeline
                        activity={activity}
                        dateIso={dateIso}
                        onOpenEvent={(ev) => { setActiveEvent(ev); setScreen('event_detail'); }}
                        onPickFinance={() => setScreen('finance')}
                        onPickPai={() => setScreen('omnis')}
                    />
                )}
                {screen === 'events' && (
                    <EventsScreen
                        events={events}
                        onOpen={(ev) => { setActiveEvent(ev); setScreen('event_detail'); }}
                        onNew={() => { setActiveEvent(null); setScreen('event_form'); }}
                    />
                )}
                {screen === 'event_detail' && activeEvent && (
                    <EventDetailView event={activeEvent} onDelete={handleDeleteCurrentEvent} />
                )}
                {screen === 'event_form' && (
                    <EventForm
                        event={activeEvent}
                        dateIso={dateIso}
                        saving={savingEvent}
                        onSave={handleSaveEvent}
                        onCancel={() => setScreen(activeEvent ? 'event_detail' : 'events')}
                    />
                )}
                {screen === 'finance' && (
                    <FinanceScreen
                        activity={activity}
                        onOpenExpense={onOpenExpense}
                        onOpenBookLedger={(bookKey) => navigate(`/expenses?book=${encodeURIComponent(bookKey)}`)}
                    />
                )}
                {screen === 'omnis' && (
                    <OmnisScreen
                        activity={activity}
                        onPreviewDocument={onPreviewDocument}
                        onDownloadDocument={onDownloadDocument}
                        onRenameDocument={onRenameDocument}
                    />
                )}
            </div>
        </div>
    );
};

// Legacy export for back-compat callers.

const CalendarPanel = ({ onClose, mode, onToggleExpand }) => {
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const today = useMemo(() => new Date(), []);
    const [currentMonth, setCurrentMonth] = useState(today.getMonth());
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [selectedDate, setSelectedDate] = useState(null);
    const [activityByDate, setActivityByDate] = useState({});
    // "Now & Next" upcoming events — a DEDICATED Today+Tomorrow window fetched
    // independently of the browsed month, so the list stays anchored to the REAL
    // current day even when the grid shows another month, and survives month
    // boundaries (Upcoming events Calendar #174). Bucketed server-side by local
    // day via getActivity's tz param.
    const [upcomingByDate, setUpcomingByDate] = useState({});
    // Event Invitations dropdown (#263) — pending invitations addressed to this
    // user. Open by default so a new invitation is actually noticed; the whole
    // section is unmounted when there are none.
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [invitationsOpen, setInvitationsOpen] = useState(true);
    const [respondingInvitation, setRespondingInvitation] = useState(null);
    // A ticking "now" that drives the periodic re-filter — finished events drop
    // out live and the day rolls over at midnight without a page reload.
    const [nowTick, setNowTick] = useState(() => Date.now());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null); // { id, filename }
    // Upcoming Events detail pop-up (#272) — stores the id, not the event, so the
    // rendered pop-up always reflects the freshest copy from the upcoming list.
    const [upcomingDetailId, setUpcomingDetailId] = useState(null);
    // Double-booking warning (#268): { conflicts, onCancel, onConfirm }
    const [conflictPrompt, setConflictPrompt] = useState(null);
    // Deep-link target from the 24h-alert reminder: ?focus_event=<id>&edit=1
    const [deepLinkEventId, setDeepLinkEventId] = useState(null);
    const { currency: displayCurrency } = useCurrencyPreference();
    // Set true while a day-detail EDIT/CREATE form is open, so the background
    // poll (#245) never refetches the activity underneath an in-progress edit
    // and clobbers it. A ref (not state) so flipping it never triggers a render.
    const dayBusyRef = useRef(false);
    const handleDayBusyChange = useCallback((busy) => { dayBusyRef.current = !!busy; }, []);
    // The deep-link has been acted on — disarm it so nothing can re-open the
    // edit form later (#337). Stable identity: it is an effect dep in DayDetailCard.
    const handleDeepLinkConsumed = useCallback(() => setDeepLinkEventId(null), []);

    // Brief "saved" confirmation (#337). Portaled to <body> like every other
    // overlay here: the Calendar sits inside a 3D flip card, and a transformed
    // ancestor becomes the containing block for position:fixed (the #225 pattern).
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);
    const showToast = useCallback((message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ message });
        toastTimerRef.current = setTimeout(() => setToast(null), 4000);
    }, []);
    useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

    // Read the focus_event param once activity has loaded — flip open the
    // day card on the matching event's date and let DayDetailCard jump
    // straight to the event_form screen.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const eventId = params.get('focus_event');
        if (!eventId) return;
        // Find the event in the currently loaded month. If it's not in this
        // month's window we don't auto-jump (the event predates the visible
        // range); the user can navigate to its month themselves.
        let found = false;
        for (const [dateIso, slot] of Object.entries(activityByDate)) {
            const match = (slot?.events || []).find((e) => e.id === eventId);
            if (match) {
                const day = parseInt(dateIso.slice(8, 10), 10);
                if (!Number.isNaN(day)) {
                    setSelectedDate(day);
                    setDeepLinkEventId(eventId);
                }
                found = true;
                break;
            }
        }
        // Only CONSUME the deep-link once the event is actually located. On the
        // first render activityByDate is still empty (the month's events haven't
        // loaded), so stripping the param here would drop the deep-link before
        // the events arrive — the 24h-alert then opened the calendar but never
        // the edit form (24hour prior Notification bug). Leaving the param when
        // not-yet-found lets this effect re-run when activityByDate populates.
        if (!found) return;
        const next = new URLSearchParams(location.search);
        next.delete('focus_event');
        next.delete('edit');
        const qs = next.toString();
        navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
    }, [activityByDate, location.pathname, location.search, navigate]);

    const calendarGrid = useMemo(() => {
        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        const startOffset = firstDay.getDay();
        const daysInMonth = lastDay.getDate();
        const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;

        const cells = [];
        for (let i = 0; i < totalCells; i++) {
            const dayNum = i - startOffset + 1;
            const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
            const date = inMonth ? new Date(currentYear, currentMonth, dayNum) : null;
            cells.push({ dayNum: inMonth ? dayNum : null, inMonth, date });
        }
        return cells;
    }, [currentMonth, currentYear]);

    // `silent` (the background poll, #245): no loading spinner, and a failure
    // keeps the last good data instead of blanking the grid. The setter skips the
    // update when nothing changed so an unchanged poll re-renders nothing.
    const fetchActivity = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const startDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
            const lastDay = new Date(currentYear, currentMonth + 1, 0).getDate();
            const endDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
            const data = await calendarService.getActivity(startDate, endDate, displayCurrency);
            const next = data?.by_date || {};
            setActivityByDate((prev) => (sameJson(prev, next) ? prev : next));
        } catch (err) {
            if (!silent) {
                setError(userErrorMessage(err, t));
                setActivityByDate({});
            }
            // Silent poll failure is non-fatal: keep the last good activity.
        } finally {
            if (!silent) setLoading(false);
        }
    }, [currentMonth, currentYear, displayCurrency, t]);

    // Dedicated Today+Tomorrow window for the "Upcoming Events" list — decoupled
    // from the browsed month (see the upcomingByDate state note). Bucketed by
    // local day server-side (getActivity sends the active tz).
    const fetchUpcoming = useCallback(async () => {
        try {
            const tz = getActiveTimezone();
            const todayKey = localDayKey(new Date(), tz);
            const tomorrowKey = addDaysToKey(todayKey, 1);
            const data = await calendarService.getActivity(todayKey, tomorrowKey, displayCurrency);
            const next = data?.by_date || {};
            setUpcomingByDate((prev) => (sameJson(prev, next) ? prev : next));
        } catch (_) {
            // Non-fatal: keep the last good window; the grid surfaces load errors.
        }
    }, [displayCurrency]);

    // Invitations awaiting this user's answer (#263). Kept in its own fetch so a
    // failure degrades to "no dropdown" rather than breaking the month grid.
    const fetchInvitations = useCallback(async () => {
        try {
            const data = await calendarService.listPendingInvitations();
            const next = data?.invitations || [];
            setPendingInvitations((prev) => (sameJson(prev, next) ? prev : next));
        } catch (_) {
            // Non-fatal — the Bell still carries the invitation.
        }
    }, []);

    useEffect(() => {
        fetchActivity();
    }, [fetchActivity]);

    useEffect(() => {
        fetchUpcoming();
    }, [fetchUpcoming]);

    useEffect(() => {
        fetchInvitations();
    }, [fetchInvitations]);

    // Refetch when chat-driven actions change events / expenses underneath us.
    // One omnis:* event fires per row; trailing-debounce so a burst → one refetch.
    // A timezone change re-buckets both windows by the new local day.
    useEffect(() => {
        const handler = debounce(() => {
            fetchActivity(); fetchUpcoming(); fetchInvitations();
        }, 300);
        window.addEventListener('omnis:calendar-event-created', handler);
        window.addEventListener('omnis:calendar-event-updated', handler);
        window.addEventListener('omnis:expense-created', handler);
        window.addEventListener('omnis:expense-updated', handler);
        // The chat delete card fires only -deleted (#438); without this the day's
        // finance box kept a transaction that no longer existed.
        window.addEventListener('omnis:expense-deleted', handler);
        window.addEventListener('omnis:timezone-changed', handler);
        return () => {
            handler.cancel();
            window.removeEventListener('omnis:calendar-event-created', handler);
            window.removeEventListener('omnis:calendar-event-updated', handler);
            window.removeEventListener('omnis:expense-created', handler);
            window.removeEventListener('omnis:expense-updated', handler);
            window.removeEventListener('omnis:expense-deleted', handler);
            window.removeEventListener('omnis:timezone-changed', handler);
        };
    }, [fetchActivity, fetchUpcoming, fetchInvitations]);

    // Real-time cleanup + midnight rollover (Upcoming events Calendar #174).
    // Every 30s (and on window focus) advance `nowTick` so finished events drop
    // out of the Upcoming list live; when the LOCAL day changes (midnight),
    // refetch the Today/Tomorrow window so the blocks roll over ("old Tomorrow"
    // → Today, and a new Tomorrow appears).
    useEffect(() => {
        const REFILTER_MS = 30 * 1000;
        let lastDayKey = localDayKey(new Date(), getActiveTimezone());
        const tick = () => {
            setNowTick(Date.now());
            const nowKey = localDayKey(new Date(), getActiveTimezone());
            if (nowKey !== lastDayKey) {
                lastDayKey = nowKey;
                fetchUpcoming();
            }
        };
        const id = setInterval(tick, REFILTER_MS);
        window.addEventListener('focus', tick);
        return () => {
            clearInterval(id);
            window.removeEventListener('focus', tick);
        };
    }, [fetchUpcoming]);

    // Cross-channel live refresh (Ui calendar Update #245). A calendar change
    // made through an external channel (Telegram) writes straight to the
    // DB and fires NO browser window event, so the open web Calendar never heard
    // about it — the user had to close and re-open the panel. While this panel is
    // mounted we poll the SAME data sources silently every CALENDAR_POLL_MS (only
    // when the tab is visible), plus refetch immediately on focus / visibility
    // regain. The activity poll is SKIPPED while a day-detail edit form is open
    // (dayBusyRef) so it can't clobber an in-progress edit; the Today/Tomorrow
    // upcoming window (hidden during a day-edit) still refreshes. Both setters
    // skip unchanged data, so an unchanged poll causes zero re-render (no flicker).
    useEffect(() => {
        const poll = () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            if (!dayBusyRef.current) fetchActivity({ silent: true });
            fetchUpcoming();
        };
        const id = setInterval(poll, CALENDAR_POLL_MS);
        const onVisibility = () => { if (document.visibilityState === 'visible') poll(); };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', poll);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', poll);
        };
    }, [fetchActivity, fetchUpcoming]);

    const upcomingEvents = useMemo(() => {
        const now = new Date(nowTick);
        const nowMs = now.getTime();
        const tz = getActiveTimezone();
        // Strict scope: ONLY events whose LOCAL day is Today or Tomorrow. The BE
        // buckets events by local day, so the slot key IS the local calendar day.
        const todayKey = localDayKey(now, tz);
        const tomorrowKey = addDaysToKey(todayKey, 1);
        const candidates = [
            ...((upcomingByDate[todayKey]?.events) || []),
            ...((upcomingByDate[tomorrowKey]?.events) || []),
        ];

        const kept = candidates.filter((ev) => {
            // Date-only "Time to be confirmed" events (pinned to local midnight,
            // no real end time) stay visible for their WHOLE day — they're still
            // actionable and must NOT auto-remove at 00:01 (documented design
            // decision, #174). They naturally leave the list once the day rolls
            // over (they're no longer in the Today/Tomorrow slots).
            if (isTimePending(ev)) return true;
            // A timed event disappears the moment it finishes (now > end_time,
            // or start + 1h when it has no end).
            return !timedEventHasFinished(ev, nowMs);
        });

        // Sort by calendar day; WITHIN a day, untimed ("Time to be confirmed")
        // events sort first, then timed events by time — so an untimed event
        // never lands at the 12:00 AM slot among timed ones.
        const dayOrdinal = (d) => d.getFullYear() * 10000 + d.getMonth() * 100 + d.getDate();
        return kept.sort((a, b) => {
            const da = new Date(a.start_time);
            const db = new Date(b.start_time);
            const dayDiff = dayOrdinal(da) - dayOrdinal(db);
            if (dayDiff !== 0) return dayDiff;
            const pa = isTimePending(a) ? 0 : 1;
            const pb = isTimePending(b) ? 0 : 1;
            if (pa !== pb) return pa - pb;  // untimed first
            return da - db;
        });
    }, [upcomingByDate, nowTick]);

    // The event behind the open #272 pop-up, resolved from the LIVE list by id
    // (never a snapshot) so an invitation sent from the pop-up — which refetches
    // — immediately shows the new guest, and a deleted event simply closes it.
    const upcomingDetail = useMemo(
        () => (upcomingDetailId ? upcomingEvents.find((e) => e.id === upcomingDetailId) || null : null),
        [upcomingDetailId, upcomingEvents],
    );

    const handlePrevMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear((y) => y - 1);
        } else {
            setCurrentMonth((m) => m - 1);
        }
        setSelectedDate(null);
    };

    const handleNextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear((y) => y + 1);
        } else {
            setCurrentMonth((m) => m + 1);
        }
        setSelectedDate(null);
    };

    const handleDayClick = (dayNum) => {
        if (!dayNum) return;
        // Calendar Bug §I — full, unrestricted navigation: ANY in-month day
        // (past, present or future) flips open the day-detail card so the user
        // can inspect it or add a manual entry, even with no prior activity.
        // Empty days simply land on the menu showing "No events scheduled".
        setSelectedDate(dayNum);
    };

    const handleCloseDetail = () => setSelectedDate(null);

    // Sequential day navigation inside the open Daily View (Date arrows ticket):
    // step ±1 day, rolling over month/year boundaries. Building a Date with an
    // out-of-range day normalizes it (e.g. June 31 → July 1, June 0 → May 31);
    // changing the month refetches that month's activity, so the open card's
    // content refreshes for the new day without flipping back.
    const navigateDay = (delta) => {
        if (!selectedDate) return;
        const d = new Date(currentYear, currentMonth, selectedDate + delta);
        setCurrentYear(d.getFullYear());
        setCurrentMonth(d.getMonth());
        setSelectedDate(d.getDate());
    };

    const isToday = (dayNum) => (
        dayNum &&
        currentYear === today.getFullYear() &&
        currentMonth === today.getMonth() &&
        dayNum === today.getDate()
    );

    const getDayMarkers = (dayNum) => {
        if (!dayNum) return null;
        const key = dateKey(currentYear, currentMonth, dayNum);
        const slot = activityByDate[key];
        if (!slot) return null;
        // Gold dot = any transaction (expense OR income) — see Calendar
        // section of the Transaction Motor spec.
        return {
            hasExpenses: (slot.expense_count || 0) + (slot.income_count || 0) > 0,
            hasEvents: (slot.events && slot.events.length > 0),
            hasUsage: (slot.documents_uploaded || 0) + (slot.documents_edited || 0) > 0,
        };
    };

    // Run a save; on the BE's double-booking 409 (#268) put the clash in front of
    // the user and only proceed if they choose "Book anyway", which replays the
    // SAME request with force:true. Resolves to null when they cancel, so nothing
    // is created/changed. Any other error propagates to the caller's handler.
    const saveWithConflictGuard = useCallback(async (attempt) => {
        try {
            return await attempt(false);
        } catch (err) {
            if (!isConflictError(err)) throw err;
            return await new Promise((resolve) => {
                setConflictPrompt({
                    conflicts: err.body?.conflicts || [],
                    onCancel: () => { setConflictPrompt(null); resolve(null); },
                    onConfirm: async () => {
                        setConflictPrompt(null);
                        try {
                            resolve(await attempt(true));
                        } catch (retryErr) {
                            setError(retryErr.message || t('caleErrSaveEvent', 'Failed to save event'));
                            resolve(null);
                        }
                    },
                });
            });
        }
    }, [t]);

    const handleUpdateEvent = async (eventId, updates) => {
        try {
            const saved = await saveWithConflictGuard((force) =>
                calendarService.updateEvent(eventId, force ? { ...updates, force: true } : updates));
            if (saved) {
                await fetchActivity();
                showToast(t('caleEventSavedToast', 'Event saved'));
            }
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    const handleCreateEvent = async (payload) => {
        try {
            const created = await saveWithConflictGuard((force) =>
                calendarService.createEvent(force ? { ...payload, force: true } : payload));
            if (created) {
                await fetchActivity();
                showToast(t('caleEventSavedToast', 'Event saved'));
            }
            return created;
        } catch (err) {
            setError(userErrorMessage(err, t));
            return null;
        }
    };

    // Dispatch invitations for a picker selection (#271 "Save and Invite", #272
    // the upcoming-event pop-up's Invite). Teams are expanded to their member
    // rosters and the whole list de-duplicated before a single invitation goes
    // out per person; refreshing BOTH windows + broadcasting is what makes the
    // new guest appear on every open surface without a reload (#56).
    const handleInviteToEvent = useCallback(async (eventId, selection) => {
        const userIds = await resolveInviteeUserIds(selection);
        if (!userIds.length) return { invited: 0, skipped: 0, failed: [] };
        const result = await inviteUsersToEvent(eventId, userIds);
        await Promise.all([fetchActivity(), fetchUpcoming()]);
        window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated'));
        if (result.failed.length) {
            setError(t('caleCouldntInviteNofM', "Couldn't invite {failed} of {total} {person}.", {
                failed: result.failed.length,
                total: userIds.length,
                person: userIds.length === 1 ? t('calePersonSingular', 'person') : t('calePeoplePlural', 'people'),
            }));
        }
        return result;
    }, [fetchActivity, fetchUpcoming, t]);

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm(t('caleConfirmDeleteEvent', 'Are you sure you want to delete this event?'))) return;
        try {
            await calendarService.deleteEvent(eventId);
            await fetchActivity();
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    // Upcoming-widget update: after saving the inline edit, refresh BOTH the
    // browsed-month grid and the Today/Tomorrow window, and broadcast so the
    // bell / other views re-sync (#235).
    const handleUpcomingUpdateEvent = async (eventId, updates) => {
        try {
            const saved = await saveWithConflictGuard((force) =>
                calendarService.updateEvent(eventId, force ? { ...updates, force: true } : updates));
            if (!saved) return;   // user cancelled at the conflict warning (#268)
            await Promise.all([fetchActivity(), fetchUpcoming()]);
            window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated'));
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    // Event Invitations dropdown: answer inline (#263). On accept the BE also
    // dual-writes the event onto this user's calendar (#264), so refreshing both
    // windows here is what makes it appear "immediately, without a refresh".
    const handleInvitationResponse = async (invitationId, decision) => {
        setRespondingInvitation(invitationId);
        try {
            await calendarService.respondToInvitation(invitationId, decision);
            // Drop it from the list straight away — the row is resolved whatever
            // the refetch says, and waiting would leave a dead button on screen.
            setPendingInvitations((prev) =>
                prev.filter((i) => i.invitation_id !== invitationId));
            await Promise.all([fetchActivity(), fetchUpcoming(), fetchInvitations()]);
            window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated'));
        } catch (err) {
            setError(userErrorMessage(err, t));
            fetchInvitations();
        } finally {
            setRespondingInvitation(null);
        }
    };

    // Upcoming-widget delete: a small widget bin action — direct delete (no
    // confirmation dialog per the ticket), then refresh both windows + broadcast
    // so the month grid updates too (#235).
    const handleUpcomingDeleteEvent = async (eventId) => {
        try {
            await calendarService.deleteEvent(eventId);
            await Promise.all([fetchActivity(), fetchUpcoming()]);
            window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated'));
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    // Click an expense → jump to ExpensesPanel with the right book pre-selected
    // and the matching card highlighted + scrolled into view.
    const handleOpenExpense = (expense) => {
        const params = new URLSearchParams();
        params.set('focus', expense.id);
        params.set('book', expense.book_id || 'unassigned');
        navigate(`/expenses?${params.toString()}`);
    };

    const handlePreviewDocument = (doc) => {
        setPreviewDoc({ id: doc.id, filename: doc.filename });
    };

    const handleDownloadDocument = async (doc) => {
        try {
            await documentService.downloadDocument(doc.id, doc.filename);
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    const handleRenameDocument = async (doc, newName) => {
        try {
            await documentService.renameDocument(doc.id, newName);
            // Patch the in-memory activity map so the new name shows up
            // immediately without waiting for a refetch.
            setActivityByDate((prev) => {
                const next = { ...prev };
                for (const key of Object.keys(next)) {
                    const slot = next[key];
                    const updateList = (list) =>
                        list.map((d) => (d.id === doc.id ? { ...d, filename: newName } : d));
                    next[key] = {
                        ...slot,
                        documents_uploaded_list: updateList(slot.documents_uploaded_list || []),
                        documents_edited_list: updateList(slot.documents_edited_list || []),
                    };
                }
                return next;
            });
        } catch (err) {
            setError(userErrorMessage(err, t));
            throw err;
        }
    };

    const selectedKey = selectedDate
        ? dateKey(currentYear, currentMonth, selectedDate)
        : null;
    const selectedActivity = selectedKey ? activityByDate[selectedKey] : null;
    const selectedDateLabel = selectedDate
        ? `${MONTHS[currentMonth]} ${selectedDate}, ${currentYear}`
        : '';
    const isFlipped = Boolean(selectedDate);

    return (
        <PanelShell
            title={t('calendar', 'Calendar')}
            icon={<Calendar size={18} />}
            mode={mode}
            onClose={onClose}
            onToggleExpand={onToggleExpand}
        >
            {/* When a day is OPEN the whole content becomes a full-height flex
                column so the day-detail fills the ENTIRE panel (no "gap of
                nothingness" below the modules — Day fixes V2 #233). In the month
                view it keeps its natural, scrollable flow. */}
            <div className={isFlipped ? 'p-4 h-full flex flex-col min-h-0' : 'p-4 space-y-4'}>
                {error && (
                    <div className="bg-pai-red/[0.08] border border-pai-red/30 rounded-card p-3 text-sm text-pai-red flex-shrink-0">
                        {error}
                    </div>
                )}

                {/* 3D flip card: front = month grid, back = day detail. When
                    flipped it grows to consume all remaining height (flex-1) so
                    the day-detail (and its sticky module footer) reach the bottom. */}
                <div
                    className={`relative ${isFlipped ? 'flex-1 min-h-0' : ''}`}
                    style={{ perspective: '1600px' }}
                >
                    <div
                        className={`relative w-full transition-transform duration-700 ease-in-out ${isFlipped ? 'h-full' : ''}`}
                        style={{
                            transformStyle: 'preserve-3d',
                            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                            minHeight: isFlipped ? undefined : '520px',
                        }}
                    >
                        {/* Front face — the month grid */}
                        <div
                            className={`pai-card px-6 py-[22px] ${isFlipped ? 'pointer-events-none' : ''}`}
                            style={{
                                backfaceVisibility: 'hidden',
                                WebkitBackfaceVisibility: 'hidden',
                            }}
                            aria-hidden={isFlipped}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <button
                                    onClick={handlePrevMonth}
                                    className="pai-icon-btn w-10 h-10 flex-shrink-0"
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <h2 className="text-[26px] font-extrabold tracking-[-0.025em] text-white truncate px-2">
                                    {MONTHS[currentMonth]} {currentYear}
                                </h2>
                                <button
                                    onClick={handleNextMonth}
                                    className="pai-icon-btn w-10 h-10 flex-shrink-0"
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </div>

                            <div className="grid grid-cols-7 pb-3">
                                {DAYS_OF_WEEK.map((d) => (
                                    <div key={d} className="text-center text-[13.5px] font-medium text-pai-meta">
                                        {d}
                                    </div>
                                ))}
                            </div>

                            {loading ? (
                                <div className="flex items-center justify-center py-10 text-pai-meta text-sm">
                                    {t('caleLoadingEllipsis', 'Loading...')}
                                </div>
                            ) : (
                                /* Hairline grid: border-top/left on the container,
                                   border-right/bottom on every cell. */
                                <div className="grid grid-cols-7 border-t border-l border-white/[0.09] rounded-[10px] overflow-hidden">
                                    {calendarGrid.map((cell, idx) => {
                                        const markers = getDayMarkers(cell.dayNum);
                                        const todayCell = isToday(cell.dayNum);

                                        return (
                                            <button
                                                key={idx}
                                                onClick={() => handleDayClick(cell.dayNum)}
                                                disabled={!cell.inMonth}
                                                // `ring-2` is load-bearing: 14 specs click
                                                // today's cell via `button.ring-2`. It is
                                                // INSET so the container's overflow-hidden
                                                // cannot clip it.
                                                className={`min-h-[92px] flex flex-col items-center justify-center gap-2.5 pt-3 relative border-r border-b border-white/[0.09] transition-colors
                                                    ${cell.inMonth ? 'cursor-pointer hover:bg-white/[0.045]' : 'cursor-default'}
                                                    ${todayCell ? 'ring-2 ring-inset ring-white/80 bg-white/[0.055] shadow-glow' : ''}`}
                                            >
                                                <span className={`text-[17px] font-bold tabular-nums ${cell.inMonth ? 'text-white' : 'text-transparent'}`}>
                                                    {cell.inMonth ? cell.dayNum : ''}
                                                </span>
                                                {markers && (
                                                    <div className="flex gap-1.5 h-[9px]">
                                                        {markers.hasExpenses && (
                                                            <span
                                                                className="w-[9px] h-[9px] rounded-full bg-pai-amber"
                                                                title={t('caleTransactionsMarker', 'Transactions (income / expense)')}
                                                            />
                                                        )}
                                                        {markers.hasEvents && (
                                                            <span
                                                                className="w-[9px] h-[9px] rounded-full bg-pai-green"
                                                                title={t('caleEventsWord', 'Events')}
                                                            />
                                                        )}
                                                        {markers.hasUsage && (
                                                            <span
                                                                className="w-[9px] h-[9px] rounded-full bg-pai-blue"
                                                                title={t('caleDocumentActivity', 'Document activity')}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Dot legend — the three event classes, centred beneath the
                                grid with hairline rules between. `flex-wrap` keeps it
                                inside the narrowest panel (#243) rather than overflowing. */}
                            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-[34px] gap-y-2 text-[14px] font-medium text-white/[0.72]">
                                <span className="inline-flex items-center gap-2">
                                    <span className="w-[11px] h-[11px] rounded-full bg-pai-amber" /> {t('caleTransactionsLegend', 'Transactions')}
                                </span>
                                <span aria-hidden="true" className="w-px h-4 bg-white/[0.12]" />
                                <span className="inline-flex items-center gap-2">
                                    <span className="w-[11px] h-[11px] rounded-full bg-pai-green" /> {t('caleEventsWord', 'Events')}
                                </span>
                                <span aria-hidden="true" className="w-px h-4 bg-white/[0.12]" />
                                <span className="inline-flex items-center gap-2">
                                    <span className="w-[11px] h-[11px] rounded-full bg-pai-blue" /> {t('caleUsageLegend', 'Usage')}
                                </span>
                            </div>
                        </div>

                        {/* Back face — the day detail */}
                        <div
                            className={`absolute inset-0 overflow-hidden pai-card ${!isFlipped ? 'pointer-events-none' : ''}`}
                            style={{
                                backfaceVisibility: 'hidden',
                                WebkitBackfaceVisibility: 'hidden',
                                transform: 'rotateY(180deg)',
                            }}
                            aria-hidden={!isFlipped}
                        >
                            {selectedDate && (
                                <DayDetailCard
                                    dateLabel={selectedDateLabel}
                                    dateIso={dateKey(currentYear, currentMonth, selectedDate)}
                                    activity={selectedActivity}
                                    initialEventId={deepLinkEventId}
                                    onPrevDay={() => navigateDay(-1)}
                                    onNextDay={() => navigateDay(1)}
                                    onClose={() => { setDeepLinkEventId(null); handleCloseDetail(); }}
                                    onUpdateEvent={handleUpdateEvent}
                                    onCreateEvent={handleCreateEvent}
                                    onInviteToEvent={handleInviteToEvent}
                                    onDeleteEvent={handleDeleteEvent}
                                    onOpenExpense={handleOpenExpense}
                                    onPreviewDocument={handlePreviewDocument}
                                    onDownloadDocument={handleDownloadDocument}
                                    onRenameDocument={handleRenameDocument}
                                    onBusyChange={handleDayBusyChange}
                                    onDeepLinkConsumed={handleDeepLinkConsumed}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Event Invitations (#263) — the Calendar's discovery surface for
                    invitations awaiting an answer. Positioned underneath the month
                    grid and directly ABOVE Upcoming Events, per the card. The whole
                    section is UNMOUNTED when there is nothing pending (not merely
                    collapsed), and the list scrolls once there are several.
                    Hidden while a day is open, matching the sibling Upcoming Events
                    section (#175) so the two never disagree about the day view. */}
                {!isFlipped && pendingInvitations.length > 0 && (
                    <div
                        data-testid="event-invitations"
                        className="pai-card"
                    >
                        <button
                            type="button"
                            data-testid="event-invitations-toggle"
                            onClick={() => setInvitationsOpen((v) => !v)}
                            aria-expanded={invitationsOpen}
                            className="w-full flex items-center justify-between gap-2 p-4 text-left"
                        >
                            <span className="flex items-center gap-2 min-w-0">
                                <Mail className="w-4 h-4 flex-shrink-0 text-pai-blue" />
                                <span className="text-[17px] font-bold text-white truncate">
                                    {t('caleEventInvitations', 'Event Invitations')}
                                </span>
                                <span
                                    data-testid="event-invitations-count"
                                    className="flex-shrink-0 text-xs font-semibold tnum px-2 py-0.5 rounded-full bg-pai-blue/[0.12] border border-pai-blue/30 text-pai-blue"
                                >
                                    {pendingInvitations.length}
                                </span>
                            </span>
                            <ChevronDown
                                className={`w-4 h-4 flex-shrink-0 text-pai-meta transition-transform ${invitationsOpen ? 'rotate-180' : ''}`}
                            />
                        </button>

                        {invitationsOpen && (
                            <div
                                data-testid="event-invitations-list"
                                /* `overflow-y-auto` is asserted via getComputedStyle. */
                                className="max-h-56 overflow-y-auto px-4 pb-4 space-y-2"
                            >
                                {pendingInvitations.map((inv) => {
                                    const busy = respondingInvitation === inv.invitation_id;
                                    return (
                                        <div
                                            key={inv.invitation_id}
                                            data-testid="event-invitation-row"
                                            data-invitation-id={inv.invitation_id}
                                            className="rounded-row border border-pai bg-white/[0.03] p-3 min-w-0"
                                        >
                                            <p className="text-[15px] font-semibold text-white break-words">
                                                {inv.event?.title || t('caleUntitledEvent', 'Untitled event')}
                                            </p>
                                            <p className="text-[12.5px] text-pai-meta break-words tnum">
                                                {formatFullDate(inv.event?.start_time)}
                                                {inv.event?.start_time ? ` · ${formatTime(inv.event.start_time)}` : ''}
                                                {inv.event?.location ? ` · ${inv.event.location}` : ''}
                                            </p>
                                            {inv.series?.count > 0 && (
                                                // #535: ONE entry for the whole series —
                                                // pattern, first–last date and count.
                                                <p
                                                    data-testid="event-invitation-series"
                                                    data-count={inv.series.count}
                                                    className="text-[12.5px] text-pai-secondary break-words"
                                                >
                                                    {t('caleInvitationSeries', { summary: seriesSummary(inv.series, t, language) })}
                                                </p>
                                            )}
                                            {inv.package?.count > 1 && (
                                                // #536: ONE entry for a package's invitation —
                                                // PO 2026-11-02: numbered (the guest's own,
                                                // stable numbers), Accept all / Decline all,
                                                // and the hint for answering only some in text.
                                                <div
                                                    data-testid="event-invitation-package"
                                                    data-count={inv.package.count}
                                                    className="mt-1 text-[12.5px] text-pai-secondary break-words"
                                                >
                                                    <p className="mb-1">
                                                        {t('invitePkgCount', '{n} of {total} events pending', {
                                                            n: inv.package.count,
                                                            total: inv.package.total || inv.package.count,
                                                        })}
                                                    </p>
                                                    <PackageInvitationLines
                                                        events={inv.package.events || []}
                                                        locale={language === 'es' ? 'es-ES' : 'en-GB'}
                                                        t={t}
                                                        testid="event-invitation-package-list"
                                                    />
                                                </div>
                                            )}
                                            <p
                                                data-testid="event-invitation-inviter"
                                                className="text-[12.5px] text-pai-meta break-words"
                                            >
                                                {t('caleInvitedByName', 'Invited by {name}', { name: inv.inviter_name })}
                                            </p>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                <button
                                                    type="button"
                                                    data-testid="event-invitation-confirm"
                                                    disabled={busy}
                                                    onClick={() => handleInvitationResponse(inv.invitation_id, 'confirmed')}
                                                    className="px-3 py-1.5 text-xs font-semibold rounded-ctl border border-pai-green/40 bg-pai-green/[0.1] text-pai-green hover:bg-pai-green/[0.18] disabled:opacity-50 transition-colors"
                                                >
                                                    {inv.package?.count > 1 ? t('invitePkgAcceptAll', 'Accept all') : t('commonConfirm', 'Confirm')}
                                                </button>
                                                <button
                                                    type="button"
                                                    data-testid="event-invitation-decline"
                                                    disabled={busy}
                                                    onClick={() => handleInvitationResponse(inv.invitation_id, 'declined')}
                                                    className="px-3 py-1.5 text-xs font-semibold rounded-ctl border border-pai text-pai-secondary hover:text-pai-red hover:border-pai-red/40 disabled:opacity-50 transition-colors"
                                                >
                                                    {inv.package?.count > 1 ? t('invitePkgDeclineAll', 'Decline all') : t('contactDeclineBtn', 'Decline')}
                                                </button>
                                            </div>
                                            {inv.package?.count > 1 && (
                                                <p data-testid="event-invitation-package-hint" className="mt-1.5 text-[11px] text-pai-meta break-words">
                                                    {t('invitePkgHint')}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Upcoming Events — a "Now & Next" list of Today + Tomorrow events
                    (Upcoming events Calendar #174). It sits below the month grid and
                    is HIDDEN while a specific day is open (the DayDetailCard), then
                    reappears the instant that card is closed (Upcoming Events removal
                    #175). Conditional render on isFlipped drives that show/hide. */}
                {!isFlipped && (
                    /* Empty → the design's centred empty state (bold title over one
                       muted line, no illustration). Populated → the same title as a
                       normal left-aligned card heading above the list. */
                    <div
                        data-testid="upcoming-events"
                        className={`pai-card ${upcomingEvents.length === 0 ? 'px-6 py-[34px] text-center' : 'p-4'}`}
                    >
                        <h3 className={`font-bold ${upcomingEvents.length === 0 ? 'text-[18px] text-white/70' : 'text-[17px] text-white mb-3'}`}>
                            {t('caleUpcomingEvents', 'Upcoming Events')}
                        </h3>
                        {upcomingEvents.length === 0 ? (
                            <p className="mt-1.5 text-[14.5px] text-white/40">{t('caleNothingScheduled', 'Nothing scheduled for today or tomorrow.')}</p>
                        ) : (
                            <div className="space-y-2">
                                {upcomingEvents.map((ev) => (
                                    <EventCard
                                        key={ev.id}
                                        event={ev}
                                        onUpdate={handleUpcomingUpdateEvent}
                                        onDelete={handleUpcomingDeleteEvent}
                                        onOpen={(target) => setUpcomingDetailId(target.id)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Upcoming-event detail pop-up (#272). The event is looked up from
                the live upcoming list rather than snapshotted, so an invite (or
                a background refresh) flows straight into the open pop-up; if the
                event disappears (deleted elsewhere) nothing renders. */}
            {upcomingDetail && (
                <UpcomingEventDetail
                    event={upcomingDetail}
                    onClose={() => setUpcomingDetailId(null)}
                    onInvite={handleInviteToEvent}
                />
            )}

            {previewDoc && (
                <DocumentPreviewModal
                    documentId={previewDoc.id}
                    documentName={previewDoc.filename}
                    onClose={() => setPreviewDoc(null)}
                />
            )}

            <ConflictDialog
                prompt={conflictPrompt}
                onCancel={() => conflictPrompt?.onCancel?.()}
                onConfirm={() => conflictPrompt?.onConfirm?.()}
            />

            {toast && createPortal(
                <div
                    data-testid="calendar-toast"
                    role="status"
                    aria-live="polite"
                    className="fixed bottom-4 right-4 z-[90] max-w-xs px-4 py-3 rounded-ctl shadow-modal-sm border text-sm inline-flex items-center gap-2 bg-pai-modal border-pai-green/40 text-white"
                >
                    <Check size={15} className="text-pai-green flex-shrink-0" />
                    <span className="min-w-0 break-words">{toast.message}</span>
                </div>,
                document.body,
            )}
        </PanelShell>
    );
};

export default CalendarPanel;
